// src/pages/EditorPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { missionsAPI } from '../utils/api';
import { escapeHtml } from '../utils/helpers';
import LevelUpOverlay from '../components/LevelUpOverlay';

// ─────────────────────────────────────────────────────────────
// MISSION ORDER — linear progression across all tracks
// ─────────────────────────────────────────────────────────────
const MISSION_ORDER = [
  'h1','h2','h3','h4','h5','h6','h7','h8',
  'c1','c2','c3','c4','c5','c6',
  'j1','j2','j3','j4','j5','j6','j7','j8',
];

function getNextMissionId(currentId) {
  const idx = MISSION_ORDER.indexOf(currentId);
  if (idx === -1 || idx === MISSION_ORDER.length - 1) return null;
  return MISSION_ORDER[idx + 1];
}

// ─────────────────────────────────────────────────────────────
// CLIENT-SIDE VALIDATION ENGINE
// ─────────────────────────────────────────────────────────────
function validateCode(code, rules) {
  const errors = [];
  for (const r of (rules || [])) {
    let pass = false;
    try {
      switch (r.type) {
        case 'contains_tag':  pass = new RegExp(`<${r.value}[\\s>/]`,'i').test(code); break;
        case 'contains_attr': pass = new RegExp(`${r.value}\\s*=`,'i').test(code);    break;
        case 'contains_text': pass = code.toLowerCase().includes((r.value||'').toLowerCase()); break;
        case 'css_property':  pass = new RegExp(`${r.value}\\s*:`,'i').test(code);    break;
        case 'js_output':     pass = code.includes(r.value||'');                       break;
        case 'regex':         pass = new RegExp(r.value,'i').test(code);               break;
        default:              pass = true;
      }
    } catch { pass = false; }
    if (!pass) errors.push(r.message || `Check failed: ${r.type}`);
  }
  return { passed: errors.length === 0, errors };
}

// ─────────────────────────────────────────────────────────────
// MISSION DATA  (all 16 missions — HTML, CSS, JavaScript)
// Each mission has: instructions, hint, solution, starterCode
// ─────────────────────────────────────────────────────────────
const LOCAL_MISSIONS = {

  /* ════════════════════════════════════════
     HTML TRACK  (h1 – h8)
  ════════════════════════════════════════ */

  h1: {
    title: 'Your First HTML Page',
    xpReward: 50, coinsReward: 20,
    instructions: `
<div class="mi-track">🌐 HTML — Mission 1 of 8</div>
<h2 class="mi-title">Your First HTML Page</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT IS HTML?</div>
  <p>HTML (HyperText Markup Language) is the skeleton of every webpage you have ever seen. 
  It uses <strong>tags</strong> like <code>&lt;html&gt;</code> and <code>&lt;body&gt;</code> 
  to describe the structure of a page. Every HTML file must start with a standard 
  boilerplate — think of it as the "template" that every page is built on.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Write a complete HTML boilerplate. Your file must contain all 4 required pieces:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>DOCTYPE declaration</strong><br>
      <code>&lt;!DOCTYPE html&gt;</code> — must be the very <em>first line</em>.
      It tells the browser this is an HTML5 document.
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Root element</strong><br>
      <code>&lt;html&gt;</code> — wraps the entire page. Every other tag lives inside this one.
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Head section</strong><br>
      <code>&lt;head&gt;</code> — contains invisible metadata like the page title and CSS links.
      It goes <em>inside</em> <code>&lt;html&gt;</code>.
    </div>
  </li>
  <li>
    <span class="mi-step-num">4</span>
    <div>
      <strong>Body section</strong><br>
      <code>&lt;body&gt;</code> — contains everything the user actually <em>sees</em>.
      It goes <em>after</em> <code>&lt;head&gt;</code>, still inside <code>&lt;html&gt;</code>.
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ STRUCTURE DIAGRAM</div>
  <pre>└── &lt;!DOCTYPE html&gt;
└── &lt;html&gt;
    ├── &lt;head&gt; ... &lt;/head&gt;    ← metadata (invisible)
    └── &lt;body&gt; ... &lt;/body&gt;    ← page content (visible)</pre>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>&lt;!DOCTYPE html&gt;</code> on line 1</li>
    <li>☐ <code>&lt;html&gt;</code> wrapping everything</li>
    <li>☐ <code>&lt;head&gt;</code> inside html</li>
    <li>☐ <code>&lt;body&gt;</code> inside html, after head</li>
  </ul>
</div>`,
    hint: `💡 The structure is always the same order:
1. <!DOCTYPE html>  ← first, always
2. <html>           ← opens
3.   <head></head>  ← metadata
4.   <body></body>  ← content
5. </html>          ← closes

Every tag that opens must also close: <html>...</html>`,
    solution: `<!DOCTYPE html>\n<html>\n  <head>\n    <title>My First Page</title>\n  </head>\n  <body>\n    <h1>Hello, World!</h1>\n    <p>This is my first HTML page!</p>\n  </body>\n</html>`,
    starterCode: '<!-- Write your HTML boilerplate here -->\n',
    validationRules: [
      { type:'contains_text', value:'<!doctype html>', message:'Add <!DOCTYPE html> as the very first line' },
      { type:'contains_tag',  value:'html',            message:'Add <html> as the root element' },
      { type:'contains_tag',  value:'head',            message:'Add a <head> section inside <html>' },
      { type:'contains_tag',  value:'body',            message:'Add a <body> section inside <html>' },
    ],
  },

  h2: {
    title: 'Titles & Headings',
    xpReward: 50, coinsReward: 20,
    instructions: `
<div class="mi-track">🌐 HTML — Mission 2 of 8</div>
<h2 class="mi-title">Titles & Headings</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE TITLES & HEADINGS?</div>
  <p>HTML has two different ways to give a page a "title":</p>
  <ul>
    <li><strong>Browser tab title</strong> — set with <code>&lt;title&gt;</code> in the <code>&lt;head&gt;</code>. Users see it in the tab bar. Search engines use it too.</li>
    <li><strong>Page heading</strong> — set with <code>&lt;h1&gt;</code> to <code>&lt;h6&gt;</code> in the <code>&lt;body&gt;</code>. This is the big visible text on the page.</li>
  </ul>
  <p>These are <em>completely different</em> — beginners often confuse them!</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Add both a browser tab title and a visible heading to your page:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Add a &lt;title&gt; tag inside &lt;head&gt;</strong><br>
      <code>&lt;title&gt;My Awesome Page&lt;/title&gt;</code><br>
      <span class="mi-note">This text appears in the browser tab. You won't see it on the page itself.</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add an &lt;h1&gt; heading inside &lt;body&gt;</strong><br>
      <code>&lt;h1&gt;Welcome to My Page!&lt;/h1&gt;</code><br>
      <span class="mi-note">h1 is the biggest heading. Use only one h1 per page — it's the main topic.</span>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 TITLE vs HEADING — KEY DIFFERENCES</div>
  <table class="mi-table">
    <tr><th>Feature</th><th>&lt;title&gt;</th><th>&lt;h1&gt;</th></tr>
    <tr><td>Where it goes</td><td>Inside &lt;head&gt;</td><td>Inside &lt;body&gt;</td></tr>
    <tr><td>Visible on page</td><td>No (tab only)</td><td>Yes</td></tr>
    <tr><td>Used by search engines</td><td>Yes (important!)</td><td>Yes</td></tr>
    <tr><td>Example</td><td>&lt;title&gt;My Blog&lt;/title&gt;</td><td>&lt;h1&gt;Welcome!&lt;/h1&gt;</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>📐 Heading levels:</strong> h1 = biggest, h6 = smallest. 
  Think of them like a document outline: h1 is the chapter title, h2 is section, h3 is sub-section, etc.
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>&lt;title&gt;</code> tag inside <code>&lt;head&gt;</code> with text between the tags</li>
    <li>☐ <code>&lt;h1&gt;</code> tag inside <code>&lt;body&gt;</code> with text between the tags</li>
  </ul>
</div>`,
    hint: `💡 Remember: title goes in <head>, h1 goes in <body>.

<head>
  <title>Text shown in browser tab</title>   ← here
</head>
<body>
  <h1>Big visible heading on the page</h1>  ← here
</body>`,
    solution: `<!DOCTYPE html>\n<html>\n  <head>\n    <title>My Awesome Page</title>\n  </head>\n  <body>\n    <h1>Welcome to My Page!</h1>\n    <h2>About This Page</h2>\n    <p>This page has a title and headings.</p>\n  </body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n  <head>\n    <!-- Add your <title> tag here -->\n  </head>\n  <body>\n    <!-- Add your <h1> heading here -->\n  </body>\n</html>`,
    validationRules: [
      { type:'contains_tag', value:'title', message:'Add a <title> tag inside <head>' },
      { type:'contains_tag', value:'h1',    message:'Add an <h1> heading inside <body>' },
    ],
  },

  h3: {
    title: 'Paragraphs & Sub-headings',
    xpReward: 60, coinsReward: 25,
    instructions: `
<div class="mi-track">🌐 HTML — Mission 3 of 8</div>
<h2 class="mi-title">Paragraphs & Sub-headings</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE PARAGRAPHS?</div>
  <p>Text on a webpage is organized into <strong>paragraphs</strong> using <code>&lt;p&gt;</code> tags. 
  Without paragraph tags, all your text would merge into one big blob — HTML ignores 
  line breaks and extra spaces in your code!</p>
  <p>Sub-headings (<code>&lt;h2&gt;</code> through <code>&lt;h6&gt;</code>) organize 
  content into sections, just like a book has chapters and sub-chapters.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Build a structured page with headings and paragraphs:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Add an &lt;h2&gt; sub-heading</strong><br>
      <code>&lt;h2&gt;About My Hobbies&lt;/h2&gt;</code><br>
      <span class="mi-note">h2 is a section heading, one level below h1. Use h2 for major sections of a page.</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add at least two &lt;p&gt; paragraphs</strong><br>
      <code>&lt;p&gt;I love coding and building websites.&lt;/p&gt;</code><br>
      <code>&lt;p&gt;I also enjoy reading and hiking.&lt;/p&gt;</code><br>
      <span class="mi-note">Each &lt;p&gt; creates a new paragraph with spacing above and below it.</span>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">📐 HEADING HIERARCHY</div>
  <pre>h1 — Page Title         (biggest, use once)
  h2 — Section Heading
    h3 — Sub-section
      h4 — Sub-sub-section
        h5 — Minor heading
          h6 — Smallest heading</pre>
</div>

<div class="mi-note-box">
  <strong>⚠️ Common mistake:</strong> Don't skip levels! Don't jump from h1 straight to h4. 
  Go h1 → h2 → h3 in order, just like an outline.
</div>

<div class="mi-example">
  <div class="mi-section-label">💡 REAL-WORLD EXAMPLE</div>
  <pre>&lt;h1&gt;My Travel Blog&lt;/h1&gt;
&lt;h2&gt;Japan Trip 2024&lt;/h2&gt;
&lt;p&gt;I visited Tokyo for the first time...&lt;/p&gt;
&lt;p&gt;The food was absolutely incredible!&lt;/p&gt;
&lt;h2&gt;Italy Trip 2023&lt;/h2&gt;
&lt;p&gt;Rome was breathtaking...&lt;/p&gt;</pre>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ At least one <code>&lt;h2&gt;</code> sub-heading with text</li>
    <li>☐ At least two <code>&lt;p&gt;</code> paragraphs with text content</li>
    <li>☐ All tags properly opened and closed</li>
  </ul>
</div>`,
    hint: `💡 Structure your page like an article:

<h1>Main Title</h1>
<h2>Section 1</h2>
<p>First paragraph text goes here.</p>
<p>Second paragraph text goes here.</p>
<h2>Section 2</h2>
<p>More text...</p>`,
    solution: `<!DOCTYPE html>\n<html>\n<head><title>About Me</title></head>\n<body>\n  <h1>About Me</h1>\n\n  <h2>My Hobbies</h2>\n  <p>I love coding and building websites from scratch. There is something magical about turning code into a working app!</p>\n  <p>I also enjoy reading science fiction books and hiking on weekends.</p>\n\n  <h2>My Goals</h2>\n  <p>I want to become a full-stack web developer and build products that help people.</p>\n  <p>I am currently learning HTML, CSS, and JavaScript on Codrion.</p>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head><title>My Page</title></head>\n<body>\n  <h1>Welcome to My Page</h1>\n\n  <!-- Add an h2 sub-heading here -->\n\n  <!-- Add two paragraphs here -->\n\n</body>\n</html>`,
    validationRules: [
      { type:'contains_tag', value:'h2', message:'Add an <h2> sub-heading' },
      { type:'contains_tag', value:'p',  message:'Add at least one <p> paragraph' },
    ],
  },

  h4: {
    title: 'Hyperlinks',
    xpReward: 70, coinsReward: 30,
    instructions: `
<div class="mi-track">🌐 HTML — Mission 4 of 8</div>
<h2 class="mi-title">Hyperlinks</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE HYPERLINKS?</div>
  <p>Hyperlinks (links) are what make the web a <em>web</em>! They connect pages together. 
  Without links, every page would be an island. The <code>&lt;a&gt;</code> tag (short for "anchor") 
  creates a clickable link.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Create at least one working hyperlink on your page:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Write an &lt;a&gt; tag</strong><br>
      The anchor tag wraps the text that users will click.
      <pre>&lt;a&gt;Click here&lt;/a&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add the href attribute</strong><br>
      <code>href</code> stands for "Hypertext REFerence" — it's the destination URL.
      <pre>&lt;a href="https://google.com"&gt;Visit Google&lt;/a&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Optional: open in new tab</strong><br>
      Add <code>target="_blank"</code> to open the link in a new browser tab:
      <pre>&lt;a href="https://google.com" target="_blank"&gt;Google&lt;/a&gt;</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 TYPES OF LINKS</div>
  <table class="mi-table">
    <tr><th>Type</th><th>Example</th><th>Goes to</th></tr>
    <tr><td>External</td><td>href="https://google.com"</td><td>Another website</td></tr>
    <tr><td>Internal</td><td>href="about.html"</td><td>Another page in your site</td></tr>
    <tr><td>Anchor</td><td>href="#section1"</td><td>A section on the same page</td></tr>
    <tr><td>Email</td><td>href="mailto:hi@me.com"</td><td>Opens email app</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>⚠️ Common mistake:</strong> Forgetting <code>https://</code> in the href. 
  Writing <code>href="google.com"</code> won't work — the browser will look for a local file 
  named "google.com". Always include the full URL with <code>https://</code>.
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>&lt;a&gt;</code> tag present in the body</li>
    <li>☐ <code>href="..."</code> attribute with a full URL</li>
    <li>☐ Clickable text between the opening and closing tags</li>
  </ul>
</div>`,
    hint: `💡 The full syntax of a link:
<a href="URL">Text the user clicks</a>

Example:
<a href="https://github.com">My GitHub Profile</a>

The href value MUST start with https:// for external sites.`,
    solution: `<!DOCTYPE html>\n<html>\n<head><title>My Links</title></head>\n<body>\n  <h1>My Favourite Websites</h1>\n\n  <p>Here are some useful sites:</p>\n\n  <ul>\n    <li><a href="https://google.com" target="_blank">Google — Search Engine</a></li>\n    <li><a href="https://github.com" target="_blank">GitHub — Code Repository</a></li>\n    <li><a href="https://developer.mozilla.org" target="_blank">MDN Web Docs — HTML Reference</a></li>\n  </ul>\n\n  <p>Click any link above to visit the site!</p>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head><title>Links Page</title></head>\n<body>\n  <h1>Cool Links</h1>\n  <!-- Create a link here using the <a> tag -->\n  <!-- Example: <a href="https://...">Link text</a> -->\n</body>\n</html>`,
    validationRules: [
      { type:'contains_tag',  value:'a',    message:'Add an <a> anchor tag' },
      { type:'contains_attr', value:'href', message:'Add an href attribute with a URL' },
    ],
  },

  h5: {
    title: 'Images',
    xpReward: 70, coinsReward: 30,
    instructions: `
<div class="mi-track">🌐 HTML — Mission 5 of 8</div>
<h2 class="mi-title">Images</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT IS THE &lt;img&gt; TAG?</div>
  <p>The <code>&lt;img&gt;</code> tag embeds images into your page. Unlike most HTML tags, 
  it is a <strong>self-closing tag</strong> — it has no closing tag because it doesn't 
  wrap any content, it just inserts an image.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Add an image to your page with all required attributes:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Add an &lt;img&gt; tag</strong><br>
      This is a self-closing tag — no <code>&lt;/img&gt;</code> needed.
      <pre>&lt;img&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add the src attribute</strong><br>
      <code>src</code> (source) is the URL or file path of your image.
      <pre>&lt;img src="https://picsum.photos/400/300"&gt;</pre>
      <span class="mi-note">picsum.photos gives you free random placeholder images at any size.</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Add the alt attribute</strong><br>
      <code>alt</code> (alternative text) describes the image. This is shown when the image 
      fails to load, and is read aloud by screen readers for visually impaired users.
      <pre>&lt;img src="https://picsum.photos/400/300" alt="A beautiful landscape photo"&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">4</span>
    <div>
      <strong>Optional: set width and height</strong><br>
      <pre>&lt;img src="..." alt="..." width="400" height="300"&gt;</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 USEFUL FREE IMAGE SOURCES</div>
  <table class="mi-table">
    <tr><th>Service</th><th>Example URL</th></tr>
    <tr><td>Picsum (random)</td><td>https://picsum.photos/400/300</td></tr>
    <tr><td>Picsum (specific)</td><td>https://picsum.photos/id/10/400/300</td></tr>
    <tr><td>Placeholder</td><td>https://via.placeholder.com/400x300</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>♿ Why alt text matters:</strong> About 7% of the world is visually impaired. 
  Screen reader software reads <code>alt</code> text aloud so they can understand what the image shows. 
  Always write meaningful alt text — not just "image" or "photo"!
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>&lt;img&gt;</code> tag (no closing tag needed)</li>
    <li>☐ <code>src="URL"</code> attribute pointing to an image</li>
    <li>☐ <code>alt="description"</code> attribute describing the image</li>
  </ul>
</div>`,
    hint: `💡 The complete <img> tag looks like this:
<img src="https://picsum.photos/400/300" alt="A random photo">

No closing tag! It's self-closing.
src = the URL of the image
alt = a text description (for accessibility)`,
    solution: `<!DOCTYPE html>\n<html>\n<head><title>My Gallery</title></head>\n<body>\n  <h1>My Photo Gallery</h1>\n\n  <h2>Nature</h2>\n  <img src="https://picsum.photos/400/300" alt="A beautiful nature landscape" width="400" height="300">\n  <p>A randomly selected beautiful photo.</p>\n\n  <h2>Architecture</h2>\n  <img src="https://picsum.photos/id/1029/400/300" alt="Modern building architecture" width="400" height="300">\n  <p>Stunning architectural photography.</p>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head><title>Gallery</title></head>\n<body>\n  <h1>My Gallery</h1>\n  <!-- Add an image here -->\n  <!-- Use: <img src="URL" alt="description"> -->\n</body>\n</html>`,
    validationRules: [
      { type:'contains_tag',  value:'img', message:'Add an <img> tag' },
      { type:'contains_attr', value:'src', message:'Add a src attribute with the image URL' },
      { type:'contains_attr', value:'alt', message:'Add an alt attribute describing the image' },
    ],
  },

  h6: {
    title: 'Lists',
    xpReward: 80, coinsReward: 35,
    instructions: `
<div class="mi-track">🌐 HTML — Mission 6 of 8</div>
<h2 class="mi-title">Lists</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE HTML LISTS?</div>
  <p>Lists help you present information in a clear, organized way. HTML has two main types:</p>
  <ul>
    <li><strong>Unordered list</strong> <code>&lt;ul&gt;</code> — items shown with bullet points (•). Use when order doesn't matter, like a shopping list.</li>
    <li><strong>Ordered list</strong> <code>&lt;ol&gt;</code> — items numbered automatically (1, 2, 3…). Use when sequence matters, like cooking steps.</li>
  </ul>
  <p>Both types use <code>&lt;li&gt;</code> (list item) tags for each individual item.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Create both types of lists on your page:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Create an unordered list with 3 items</strong><br>
      <pre>&lt;ul&gt;\n  &lt;li&gt;Apples&lt;/li&gt;\n  &lt;li&gt;Bananas&lt;/li&gt;\n  &lt;li&gt;Oranges&lt;/li&gt;\n&lt;/ul&gt;</pre>
      Result: • Apples  • Bananas  • Oranges
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Create an ordered list with 3 items</strong><br>
      <pre>&lt;ol&gt;\n  &lt;li&gt;Boil water&lt;/li&gt;\n  &lt;li&gt;Add tea bag&lt;/li&gt;\n  &lt;li&gt;Enjoy!&lt;/li&gt;\n&lt;/ol&gt;</pre>
      Result: 1. Boil water  2. Add tea bag  3. Enjoy!
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 UL vs OL — WHEN TO USE EACH</div>
  <table class="mi-table">
    <tr><th></th><th>&lt;ul&gt; Unordered</th><th>&lt;ol&gt; Ordered</th></tr>
    <tr><td>Bullets/Numbers</td><td>Bullet points •</td><td>Numbers 1, 2, 3</td></tr>
    <tr><td>Use when</td><td>Order doesn't matter</td><td>Order matters</td></tr>
    <tr><td>Examples</td><td>Ingredients, features, tags</td><td>Steps, rankings, instructions</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>💡 Nested lists:</strong> You can put a list inside a list item to create sub-lists! 
  Just add a &lt;ul&gt; or &lt;ol&gt; inside an &lt;li&gt;.
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ One <code>&lt;ul&gt;</code> with at least 3 <code>&lt;li&gt;</code> items</li>
    <li>☐ One <code>&lt;ol&gt;</code> with at least 3 <code>&lt;li&gt;</code> items</li>
    <li>☐ Each <code>&lt;li&gt;</code> has text content inside it</li>
  </ul>
</div>`,
    hint: `💡 Both list types follow the same pattern — only the wrapper changes:

UNORDERED (bullet points):
<ul>
  <li>Item one</li>
  <li>Item two</li>
  <li>Item three</li>
</ul>

ORDERED (numbered):
<ol>
  <li>First step</li>
  <li>Second step</li>
  <li>Third step</li>
</ol>`,
    solution: `<!DOCTYPE html>\n<html>\n<head><title>Lists</title></head>\n<body>\n  <h1>My Lists</h1>\n\n  <h2>Shopping List (unordered)</h2>\n  <ul>\n    <li>Apples</li>\n    <li>Bananas</li>\n    <li>Milk</li>\n    <li>Bread</li>\n  </ul>\n\n  <h2>How to Make Tea (ordered)</h2>\n  <ol>\n    <li>Boil water in a kettle</li>\n    <li>Pour hot water into a cup</li>\n    <li>Add a tea bag and let it steep for 3 minutes</li>\n    <li>Remove the tea bag and add milk or sugar if desired</li>\n    <li>Enjoy your tea!</li>\n  </ol>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head><title>Lists</title></head>\n<body>\n  <h1>My Lists</h1>\n\n  <!-- Create an unordered list (ul) with 3 items -->\n\n  <!-- Create an ordered list (ol) with 3 items -->\n\n</body>\n</html>`,
    validationRules: [
      { type:'contains_tag', value:'ul', message:'Add an unordered list <ul>' },
      { type:'contains_tag', value:'ol', message:'Add an ordered list <ol>' },
      { type:'contains_tag', value:'li', message:'Add list items using <li> tags' },
    ],
  },

  h7: {
    title: 'Tables',
    xpReward: 100, coinsReward: 40,
    instructions: `
<div class="mi-track">🌐 HTML — Mission 7 of 8</div>
<h2 class="mi-title">Tables</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE HTML TABLES?</div>
  <p>Tables organize data into rows and columns — like a spreadsheet. They're great for 
  schedules, price comparisons, sports standings, or any structured data.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Build a complete HTML table with headers and data rows:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Create the &lt;table&gt; wrapper</strong><br>
      Everything inside a table is wrapped in this tag.
      <pre>&lt;table&gt;\n  ...\n&lt;/table&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add a header row with &lt;th&gt; cells</strong><br>
      <code>&lt;tr&gt;</code> = table row. <code>&lt;th&gt;</code> = table header (bold + centered by default).
      <pre>&lt;tr&gt;\n  &lt;th&gt;Name&lt;/th&gt;\n  &lt;th&gt;Age&lt;/th&gt;\n  &lt;th&gt;City&lt;/th&gt;\n&lt;/tr&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Add data rows with &lt;td&gt; cells</strong><br>
      <code>&lt;td&gt;</code> = table data (regular cells).
      <pre>&lt;tr&gt;\n  &lt;td&gt;Alice&lt;/td&gt;\n  &lt;td&gt;25&lt;/td&gt;\n  &lt;td&gt;London&lt;/td&gt;\n&lt;/tr&gt;</pre>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ TABLE STRUCTURE DIAGRAM</div>
  <pre>&lt;table&gt;
  &lt;tr&gt;  ← Row 1 (headers)
    &lt;th&gt;Col 1&lt;/th&gt;  &lt;th&gt;Col 2&lt;/th&gt;
  &lt;/tr&gt;
  &lt;tr&gt;  ← Row 2 (data)
    &lt;td&gt;Val 1&lt;/td&gt;  &lt;td&gt;Val 2&lt;/td&gt;
  &lt;/tr&gt;
  &lt;tr&gt;  ← Row 3 (data)
    &lt;td&gt;Val 3&lt;/td&gt;  &lt;td&gt;Val 4&lt;/td&gt;
  &lt;/tr&gt;
&lt;/table&gt;</pre>
</div>

<div class="mi-compare">
  <div class="mi-section-label">🔍 TABLE TAGS REFERENCE</div>
  <table class="mi-table">
    <tr><th>Tag</th><th>Meaning</th><th>Use for</th></tr>
    <tr><td>&lt;table&gt;</td><td>Table container</td><td>Wraps everything</td></tr>
    <tr><td>&lt;tr&gt;</td><td>Table Row</td><td>Each horizontal row</td></tr>
    <tr><td>&lt;th&gt;</td><td>Table Header</td><td>Column headings (bold)</td></tr>
    <tr><td>&lt;td&gt;</td><td>Table Data</td><td>Regular data cells</td></tr>
  </table>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>&lt;table&gt;</code> element wrapping everything</li>
    <li>☐ At least one <code>&lt;tr&gt;</code> row with <code>&lt;th&gt;</code> headers</li>
    <li>☐ At least two <code>&lt;tr&gt;</code> rows with <code>&lt;td&gt;</code> data cells</li>
    <li>☐ Same number of cells in each row</li>
  </ul>
</div>`,
    hint: `💡 Remember the nesting order: table → tr → th/td

<table border="1">
  <tr>
    <th>Name</th>   <th>Score</th>
  </tr>
  <tr>
    <td>Alice</td>  <td>95</td>
  </tr>
  <tr>
    <td>Bob</td>    <td>87</td>
  </tr>
</table>

Add border="1" to see the table borders clearly while learning.`,
    solution: `<!DOCTYPE html>\n<html>\n<head><title>Scores Table</title></head>\n<body>\n  <h1>Student Scores</h1>\n  <table border="1" style="border-collapse: collapse; width: 400px;">\n    <tr>\n      <th style="padding: 10px;">Name</th>\n      <th style="padding: 10px;">Subject</th>\n      <th style="padding: 10px;">Score</th>\n      <th style="padding: 10px;">Grade</th>\n    </tr>\n    <tr>\n      <td style="padding: 10px;">Alice</td>\n      <td style="padding: 10px;">Maths</td>\n      <td style="padding: 10px;">95</td>\n      <td style="padding: 10px;">A+</td>\n    </tr>\n    <tr>\n      <td style="padding: 10px;">Bob</td>\n      <td style="padding: 10px;">Science</td>\n      <td style="padding: 10px;">82</td>\n      <td style="padding: 10px;">B+</td>\n    </tr>\n    <tr>\n      <td style="padding: 10px;">Charlie</td>\n      <td style="padding: 10px;">English</td>\n      <td style="padding: 10px;">78</td>\n      <td style="padding: 10px;">B</td>\n    </tr>\n  </table>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head><title>My Table</title></head>\n<body>\n  <h1>My Data Table</h1>\n  <!-- Build your table here -->\n  <!-- Structure: table > tr > th (for headers) or td (for data) -->\n</body>\n</html>`,
    validationRules: [
      { type:'contains_tag', value:'table', message:'Add a <table> element' },
      { type:'contains_tag', value:'tr',    message:'Add table rows using <tr>' },
      { type:'contains_tag', value:'td',    message:'Add table data cells using <td>' },
    ],
  },

  h8: {
    title: 'Forms',
    xpReward: 110, coinsReward: 45,
    badgeReward: { id:'html_beginner', name:'HTML Beginner', icon:'🌐' },
    instructions: `
<div class="mi-track">🌐 HTML — Mission 8 of 8  ★ BADGE MISSION</div>
<h2 class="mi-title">Forms</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE HTML FORMS?</div>
  <p>Forms are how websites collect information from users. Every login page, search bar, 
  checkout page, and contact form is built with HTML forms. Understanding forms is 
  essential for building interactive websites!</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Build a contact form with inputs and a submit button:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Create a &lt;form&gt; element</strong><br>
      The form wrapper groups all inputs together.
      <pre>&lt;form&gt;\n  ...\n&lt;/form&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add &lt;input&gt; fields</strong><br>
      Inputs are self-closing. Use <code>type</code> to change their behavior:
      <pre>&lt;input type="text" placeholder="Your name"&gt;\n&lt;input type="email" placeholder="Your email"&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Add a &lt;button&gt; to submit</strong><br>
      <pre>&lt;button type="submit"&gt;Send Message&lt;/button&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">4</span>
    <div>
      <strong>Optional: add &lt;label&gt; tags</strong><br>
      Labels describe what each field is for — important for accessibility:
      <pre>&lt;label&gt;Your Name:&lt;/label&gt;\n&lt;input type="text" placeholder="Enter name"&gt;</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 INPUT TYPE REFERENCE</div>
  <table class="mi-table">
    <tr><th>type=</th><th>What it does</th><th>Example</th></tr>
    <tr><td>"text"</td><td>Single line text</td><td>Name, username</td></tr>
    <tr><td>"email"</td><td>Email (validates format)</td><td>user@email.com</td></tr>
    <tr><td>"password"</td><td>Hides typed characters</td><td>Login forms</td></tr>
    <tr><td>"number"</td><td>Only allows numbers</td><td>Age, quantity</td></tr>
    <tr><td>"checkbox"</td><td>Tick box</td><td>Agree to terms</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>🏅 Badge reward!</strong> Complete this mission to earn the <strong>HTML Beginner</strong> badge. 
  This is the final HTML mission — great work making it here!
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>&lt;form&gt;</code> element wrapping the inputs</li>
    <li>☐ At least one <code>&lt;input&gt;</code> field</li>
    <li>☐ A <code>&lt;button&gt;</code> to submit the form</li>
  </ul>
</div>`,
    hint: `💡 A basic form structure:
<form>
  <label>Name:</label>
  <input type="text" placeholder="Your name">

  <label>Email:</label>
  <input type="email" placeholder="your@email.com">

  <button type="submit">Submit</button>
</form>

Add <br> or use CSS to put each input on its own line.`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <title>Contact Form</title>\n  <style>\n    body { font-family: Arial; max-width: 400px; margin: 40px auto; padding: 20px; }\n    label { display: block; margin-top: 14px; font-weight: bold; }\n    input, textarea { width: 100%; padding: 8px; margin-top: 4px; border: 1px solid #ccc; border-radius: 4px; }\n    button { margin-top: 16px; padding: 10px 24px; background: #4a9eff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 15px; }\n    button:hover { background: #0070ff; }\n  </style>\n</head>\n<body>\n  <h1>Contact Us</h1>\n  <form>\n    <label>Your Name:</label>\n    <input type="text" placeholder="Enter your full name" required>\n\n    <label>Your Email:</label>\n    <input type="email" placeholder="your@email.com" required>\n\n    <label>Subject:</label>\n    <input type="text" placeholder="What this is about">\n\n    <label>Message:</label>\n    <textarea rows="5" placeholder="Write your message here..."></textarea>\n\n    <button type="submit">Send Message</button>\n  </form>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head><title>Contact Form</title></head>\n<body>\n  <h1>Contact Me</h1>\n  <!-- Build your form below -->\n  <!-- Need: <form>, <input>, and <button> -->\n</body>\n</html>`,
    validationRules: [
      { type:'contains_tag', value:'form',   message:'Add a <form> element to wrap your inputs' },
      { type:'contains_tag', value:'input',  message:'Add at least one <input> field' },
      { type:'contains_tag', value:'button', message:'Add a <button> to submit the form' },
    ],
  },

  /* ════════════════════════════════════════
     CSS TRACK  (c1 – c6)
  ════════════════════════════════════════ */

  c1: {
    title: 'First Styles',
    xpReward: 60, coinsReward: 25,
    instructions: `
<div class="mi-track">🎨 CSS — Mission 1 of 6</div>
<h2 class="mi-title">First Styles</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT IS CSS?</div>
  <p>CSS (Cascading Style Sheets) is what makes HTML look beautiful. Without CSS, every website 
  would be plain black text on a white background. CSS controls colors, fonts, sizes, 
  spacing, layout — everything visual.</p>
  <p>CSS is written as <strong>rules</strong>: you pick a selector (what to style), then 
  declare properties (how to style it).</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Add your first CSS styles using a &lt;style&gt; block:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Add a &lt;style&gt; block in the &lt;head&gt;</strong><br>
      CSS rules go inside <code>&lt;style&gt;</code> tags in the head section.
      <pre>&lt;head&gt;\n  &lt;style&gt;\n    /* your CSS goes here */\n  &lt;/style&gt;\n&lt;/head&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Set a text color on the body</strong><br>
      <pre>body {\n  color: #00f5ff;\n}</pre>
      <span class="mi-note">Use a color name like "navy", a hex code like "#ff6600", or rgb(255, 100, 0).</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Set a background color</strong><br>
      <pre>body {\n  color: #00f5ff;\n  background-color: #1a1a2e;\n}</pre>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ CSS RULE ANATOMY</div>
  <pre>selector {          ← WHO to style (body, h1, .class, #id)
  property: value;  ← WHAT to change and HOW
  property: value;  ← can have many properties
}                   ← close with curly brace</pre>
</div>

<div class="mi-compare">
  <div class="mi-section-label">🔍 COLOR VALUE FORMATS</div>
  <table class="mi-table">
    <tr><th>Format</th><th>Example</th><th>Notes</th></tr>
    <tr><td>Name</td><td>color: red</td><td>140+ built-in names</td></tr>
    <tr><td>Hex</td><td>color: #ff0000</td><td>Most common format</td></tr>
    <tr><td>RGB</td><td>color: rgb(255, 0, 0)</td><td>Red, Green, Blue 0-255</td></tr>
    <tr><td>HSL</td><td>color: hsl(0, 100%, 50%)</td><td>Hue, Saturation, Lightness</td></tr>
  </table>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>&lt;style&gt;</code> block inside <code>&lt;head&gt;</code></li>
    <li>☐ <code>color:</code> property set (text color)</li>
    <li>☐ <code>background-color:</code> or <code>background:</code> property set</li>
  </ul>
</div>`,
    hint: `💡 CSS goes inside <style> tags in the <head>:

<head>
  <style>
    body {
      background-color: #1a1a2e;  /* dark blue background */
      color: #ffffff;              /* white text */
      font-family: Arial, sans-serif;
    }
    h1 {
      color: #ffd700;  /* gold headings */
    }
  </style>
</head>`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <title>Styled Page</title>\n  <style>\n    body {\n      background-color: #1a1a2e;\n      color: #e2e8f0;\n      font-family: Arial, sans-serif;\n      padding: 20px;\n    }\n    h1 {\n      color: #00f5ff;\n      font-size: 36px;\n    }\n    h2 {\n      color: #ffd700;\n    }\n    p {\n      color: #a0aec0;\n      line-height: 1.7;\n    }\n  </style>\n</head>\n<body>\n  <h1>Welcome to My Styled Page!</h1>\n  <h2>About This Page</h2>\n  <p>This page uses CSS to set colors and fonts. The background is dark, the headings are cyan and gold, and the text is light grey.</p>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head>\n  <title>Styled Page</title>\n  <style>\n    body {\n      /* Add background-color and color here */\n    }\n  </style>\n</head>\n<body>\n  <h1>Hello CSS!</h1>\n  <p>I am learning to add styles!</p>\n</body>\n</html>`,
    validationRules: [
      { type:'contains_text', value:'color',      message:'Set a color property for text' },
      { type:'contains_text', value:'background', message:'Set a background-color property' },
    ],
  },

  c2: {
    title: 'The Box Model',
    xpReward: 70, coinsReward: 30,
    instructions: `
<div class="mi-track">🎨 CSS — Mission 2 of 6</div>
<h2 class="mi-title">The Box Model</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT IS THE BOX MODEL?</div>
  <p>In CSS, <strong>every single HTML element is a rectangular box</strong>. 
  The box model describes the layers around that box — from the content outward:</p>
  <p><strong>Content → Padding → Border → Margin</strong></p>
  <p>Mastering the box model is one of the most important skills in CSS!</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Add all three spacing properties to a box element:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Add margin (space OUTSIDE the element)</strong><br>
      Margin creates space between this element and its neighbours.
      <pre>.box { margin: 20px; }  /* all 4 sides */\n.box { margin: 10px 20px; }  /* top/bottom  left/right */</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add padding (space INSIDE the element)</strong><br>
      Padding creates space between the content and the border.
      <pre>.box { padding: 20px; }</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Add a border (the outline of the element)</strong><br>
      Border syntax: <code>width style color</code>
      <pre>.box { border: 2px solid #4a9eff; }\n.box { border: 3px dashed red; }\n.box { border-radius: 8px; }  /* rounded corners */</pre>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ BOX MODEL DIAGRAM</div>
  <pre>+---------------------------+
|       MARGIN              |  ← outside (transparent)
|  +---------------------+  |
|  |      BORDER         |  |  ← the outline
|  |  +---------------+  |  |
|  |  |    PADDING    |  |  |  ← inside space
|  |  |  +---------+  |  |  |
|  |  |  | CONTENT |  |  |  |  ← actual text/image
|  |  |  +---------+  |  |  |
|  |  +---------------+  |  |
|  +---------------------+  |
+---------------------------+</pre>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>margin:</code> property on an element</li>
    <li>☐ <code>padding:</code> property on an element</li>
    <li>☐ <code>border:</code> property on an element</li>
  </ul>
</div>`,
    hint: `💡 Apply all three to the same element:

.box {
  margin: 20px;           /* space outside */
  padding: 20px;          /* space inside */
  border: 2px solid blue; /* the outline */
  border-radius: 8px;     /* optional: rounded corners */
  background-color: #eee; /* helps you see the padding */
}`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body {\n      background: #1a1a2e;\n      padding: 20px;\n      font-family: Arial;\n    }\n    .box {\n      background-color: #4a9eff;\n      color: white;\n      margin: 30px;\n      padding: 25px;\n      border: 3px solid #003080;\n      border-radius: 10px;\n      width: 250px;\n    }\n    .box2 {\n      background-color: #ff6432;\n      color: white;\n      margin: 30px 60px;\n      padding: 15px 30px;\n      border: 2px dashed white;\n      border-radius: 20px;\n    }\n  </style>\n</head>\n<body>\n  <div class="box">I have margin, padding, and a solid border!</div>\n  <div class="box2">I have different margins on each side and a dashed border!</div>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    .box {\n      background-color: #4a9eff;\n      color: white;\n      /* Add margin here */\n      /* Add padding here */\n      /* Add border here */\n    }\n  </style>\n</head>\n<body>\n  <div class="box">I am a styled box!</div>\n</body>\n</html>`,
    validationRules: [
      { type:'contains_text', value:'margin',  message:'Add a margin property' },
      { type:'contains_text', value:'padding', message:'Add a padding property' },
      { type:'contains_text', value:'border',  message:'Add a border property' },
    ],
  },

  c3: {
    title: 'Flexbox Layout',
    xpReward: 100, coinsReward: 40,
    instructions: `
<div class="mi-track">🎨 CSS — Mission 3 of 6</div>
<h2 class="mi-title">Flexbox Layout</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT IS FLEXBOX?</div>
  <p>Flexbox (Flexible Box Layout) is a CSS tool that makes it incredibly easy to 
  <strong>align and distribute elements</strong> in a row or column. 
  Before Flexbox, centering elements was famously painful. Now it's one line of CSS!</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Turn a container into a flex container and control its alignment:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Set display: flex on the PARENT container</strong><br>
      This one line activates Flexbox for all direct children.
      <pre>.container { display: flex; }</pre>
      <span class="mi-note">⚠️ Flexbox is set on the PARENT, not on the children!</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Use justify-content to align on the main axis</strong><br>
      By default, the main axis is horizontal (left to right).
      <pre>.container {\n  display: flex;\n  justify-content: center;        /* center everything */\n  justify-content: space-between; /* spread out with gaps */\n  justify-content: space-around;  /* equal space around each */\n}</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Use align-items to align on the cross axis</strong><br>
      The cross axis is perpendicular to the main axis (vertical by default).
      <pre>.container {\n  align-items: center;     /* vertically center */\n  align-items: flex-start; /* align to top */\n  align-items: stretch;    /* stretch to fill height */\n}</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 JUSTIFY-CONTENT VALUES</div>
  <table class="mi-table">
    <tr><th>Value</th><th>Effect</th></tr>
    <tr><td>flex-start</td><td>Pack items to the start (default)</td></tr>
    <tr><td>flex-end</td><td>Pack items to the end</td></tr>
    <tr><td>center</td><td>Center all items</td></tr>
    <tr><td>space-between</td><td>Max gap between items, none at edges</td></tr>
    <tr><td>space-around</td><td>Equal space around each item</td></tr>
    <tr><td>space-evenly</td><td>Equal space between and at edges</td></tr>
  </table>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>display: flex;</code> on the container</li>
    <li>☐ <code>justify-content:</code> with any value</li>
    <li>☐ <code>align-items:</code> with any value</li>
  </ul>
</div>`,
    hint: `💡 All three properties go on the PARENT container:

.container {
  display: flex;             /* activates flexbox */
  justify-content: center;   /* horizontal alignment */
  align-items: center;       /* vertical alignment */
  gap: 10px;                 /* space between items */
}

The items inside automatically become flex items.`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { background: #1a1a2e; padding: 20px; font-family: Arial; color: white; }\n    .container {\n      display: flex;\n      justify-content: space-between;\n      align-items: center;\n      background: #2a2a4e;\n      padding: 20px;\n      border-radius: 10px;\n      gap: 15px;\n      margin-bottom: 20px;\n    }\n    .item {\n      background: #4a9eff;\n      color: white;\n      padding: 20px 30px;\n      border-radius: 8px;\n      font-weight: bold;\n      font-size: 18px;\n    }\n    .center-example {\n      display: flex;\n      justify-content: center;\n      align-items: center;\n      height: 100px;\n      background: #2a2a4e;\n      border-radius: 10px;\n    }\n  </style>\n</head>\n<body>\n  <h1>Flexbox Examples</h1>\n  <h2>Space Between</h2>\n  <div class="container">\n    <div class="item">Box 1</div>\n    <div class="item">Box 2</div>\n    <div class="item">Box 3</div>\n  </div>\n  <h2>Centered</h2>\n  <div class="center-example">\n    <div class="item">I am centered!</div>\n  </div>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    .container {\n      background: #eee;\n      padding: 20px;\n      /* Add display: flex here */\n      /* Add justify-content here */\n      /* Add align-items here */\n    }\n    .item {\n      background: #4a9eff;\n      color: white;\n      padding: 20px;\n      border-radius: 8px;\n    }\n  </style>\n</head>\n<body>\n  <div class="container">\n    <div class="item">Box 1</div>\n    <div class="item">Box 2</div>\n    <div class="item">Box 3</div>\n  </div>\n</body>\n</html>`,
    validationRules: [
      { type:'contains_text', value:'display: flex',   message:'Add display: flex to the container' },
      { type:'contains_text', value:'justify-content', message:'Add a justify-content property' },
      { type:'contains_text', value:'align-items',     message:'Add an align-items property' },
    ],
  },

  c4: {
    title: 'CSS Grid',
    xpReward: 110, coinsReward: 45,
    instructions: `
<div class="mi-track">🎨 CSS — Mission 4 of 6</div>
<h2 class="mi-title">CSS Grid</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT IS CSS GRID?</div>
  <p>CSS Grid is a two-dimensional layout system — it handles both <strong>rows and columns</strong> 
  at the same time. While Flexbox is great for a single row or column, Grid is perfect for 
  full page layouts like dashboards, image galleries, and article layouts.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Create a grid layout with defined columns:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
          <strong>Set display: grid on the container</strong><br>
      <pre>.grid { display: grid; }</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Define columns with grid-template-columns</strong><br>
      <pre>/* 3 equal columns */\n.grid { grid-template-columns: repeat(3, 1fr); }\n\n/* Fixed widths */\n.grid { grid-template-columns: 200px 200px 200px; }\n\n/* Mixed */\n.grid { grid-template-columns: 200px 1fr 2fr; }</pre>
      <span class="mi-note">"1fr" means "1 fraction" — it divides available space proportionally.</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Add a gap between cells</strong><br>
      <pre>.grid { gap: 15px; }          /* same gap everywhere */\n.grid { column-gap: 10px; }    /* only between columns */\n.grid { row-gap: 20px; }       /* only between rows */</pre>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ GRID TEMPLATE COLUMNS EXAMPLES</div>
  <pre>grid-template-columns: 1fr 1fr 1fr
→ Three equal columns

grid-template-columns: repeat(3, 1fr)
→ Same as above (shorthand)

grid-template-columns: 200px 1fr
→ Sidebar (200px fixed) + content (fills rest)

grid-template-columns: repeat(4, 25%)
→ Four columns each 25% wide</pre>
</div>

<div class="mi-compare">
  <div class="mi-section-label">🔍 FLEXBOX vs GRID — WHEN TO USE EACH</div>
  <table class="mi-table">
    <tr><th></th><th>Flexbox</th><th>Grid</th></tr>
    <tr><td>Dimensions</td><td>1D (row or column)</td><td>2D (rows and columns)</td></tr>
    <tr><td>Best for</td><td>Navigation, button groups</td><td>Page layout, galleries</td></tr>
    <tr><td>Control</td><td>Items control themselves</td><td>Container controls all</td></tr>
  </table>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>display: grid;</code> on the container</li>
    <li>☐ <code>grid-template-columns:</code> defining the column layout</li>
    <li>☐ At least 4 child elements inside the grid</li>
  </ul>
</div>`,
    hint: `💡 Grid on the parent, items arrange themselves:

.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);  /* 3 equal columns */
  gap: 15px;
}

The items inside will automatically flow into the grid cells.
Add more items than columns to create multiple rows automatically!`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { background: #1a1a2e; padding: 20px; font-family: Arial; color: white; }\n    .grid {\n      display: grid;\n      grid-template-columns: repeat(3, 1fr);\n      gap: 15px;\n      margin-top: 20px;\n    }\n    .cell {\n      background: #4a9eff;\n      color: white;\n      padding: 30px;\n      text-align: center;\n      border-radius: 8px;\n      font-size: 22px;\n      font-weight: bold;\n    }\n    .cell:nth-child(even) {\n      background: #7c3aed;\n    }\n  </style>\n</head>\n<body>\n  <h1>CSS Grid Layout</h1>\n  <div class="grid">\n    <div class="cell">1</div>\n    <div class="cell">2</div>\n    <div class="cell">3</div>\n    <div class="cell">4</div>\n    <div class="cell">5</div>\n    <div class="cell">6</div>\n  </div>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    .grid {\n      background: #eee;\n      padding: 15px;\n      /* Add display: grid here */\n      /* Add grid-template-columns here */\n    }\n    .cell {\n      background: #4a9eff;\n      color: white;\n      padding: 20px;\n      text-align: center;\n    }\n  </style>\n</head>\n<body>\n  <div class="grid">\n    <div class="cell">1</div>\n    <div class="cell">2</div>\n    <div class="cell">3</div>\n    <div class="cell">4</div>\n  </div>\n</body>\n</html>`,
    validationRules: [
      { type:'contains_text', value:'display: grid',         message:'Add display: grid to the container' },
      { type:'contains_text', value:'grid-template-columns', message:'Add grid-template-columns to define the layout' },
    ],
  },

  c5: {
    title: 'Animations & Transitions',
    xpReward: 120, coinsReward: 50,
    badgeReward: { id:'css_beginner', name:'CSS Beginner', icon:'🎨' },
    instructions: `
<div class="mi-track">🎨 CSS — Mission 5 of 6  ★ BADGE MISSION</div>
<h2 class="mi-title">Animations & Transitions</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE TRANSITIONS?</div>
  <p>CSS <strong>transitions</strong> smoothly animate a property from one value to another 
  when something changes (like a hover). Without transitions, changes are instant and jarring. 
  With transitions, they're smooth and professional-looking.</p>
  <p>CSS <strong>transforms</strong> let you move, scale, rotate, or skew elements visually 
  without affecting the page layout.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Create an element that animates smoothly on hover:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
          <strong>Add transition to the element's normal state</strong><br>
      The transition goes on the ORIGINAL state, not the hover state!
      <pre>.btn {\n  transition: all 0.3s ease;\n  /* or be specific: */\n  transition: background-color 0.3s ease, transform 0.2s ease;\n}</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add transform on the :hover state</strong><br>
      <pre>.btn:hover {\n  transform: scale(1.1);         /* grow by 10% */\n  transform: translateY(-5px);   /* lift up 5px */\n  transform: rotate(5deg);       /* rotate 5 degrees */\n}</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Also change other properties on hover</strong><br>
      These will also animate smoothly thanks to the transition!
      <pre>.btn:hover {\n  background-color: #0070ff;\n  box-shadow: 0 8px 20px rgba(0,0,0,0.3);\n  transform: translateY(-3px);\n}</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 TRANSFORM VALUES</div>
  <table class="mi-table">
    <tr><th>Transform</th><th>Effect</th><th>Example</th></tr>
    <tr><td>scale(n)</td><td>Resize the element</td><td>scale(1.2) = 20% bigger</td></tr>
    <tr><td>translateX/Y(px)</td><td>Move horizontally/vertically</td><td>translateY(-5px) = up 5px</td></tr>
    <tr><td>rotate(deg)</td><td>Rotate the element</td><td>rotate(45deg)</td></tr>
    <tr><td>skew(deg)</td><td>Tilt/slant the element</td><td>skew(10deg)</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>🏅 Badge reward!</strong> Complete this mission to earn the <strong>CSS Beginner</strong> badge!
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>transition:</code> on the base state of the element</li>
    <li>☐ <code>transform:</code> on the <code>:hover</code> state</li>
    <li>☐ View the live preview to see the animation!</li>
  </ul>
</div>`,
    hint: `💡 Key rule: transition goes on the element, transform goes on :hover.

.card {
  background: #4a9eff;
  transition: all 0.3s ease;   ← on the element
}
.card:hover {
  transform: translateY(-8px); ← on the hover state
  box-shadow: 0 12px 30px rgba(0,0,0,0.3);
}

Check the PREVIEW tab to see your animation in action!`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body {\n      display: flex;\n      gap: 20px;\n      padding: 40px;\n      background: #1a1a2e;\n      flex-wrap: wrap;\n    }\n    .btn {\n      padding: 15px 30px;\n      background: #4a9eff;\n      color: white;\n      border: none;\n      border-radius: 8px;\n      cursor: pointer;\n      font-size: 16px;\n      font-weight: bold;\n      transition: all 0.3s ease;\n    }\n    .btn:hover {\n      background: #0070ff;\n      transform: scale(1.1);\n      box-shadow: 0 8px 20px rgba(74, 158, 255, 0.5);\n    }\n    .card {\n      width: 160px;\n      height: 120px;\n      background: #7c3aed;\n      border-radius: 12px;\n      display: flex;\n      align-items: center;\n      justify-content: center;\n      color: white;\n      font-weight: bold;\n      cursor: pointer;\n      transition: all 0.3s ease;\n    }\n    .card:hover {\n      transform: translateY(-10px);\n      box-shadow: 0 15px 35px rgba(124, 58, 237, 0.5);\n    }\n  </style>\n</head>\n<body>\n  <button class="btn">Hover to scale!</button>\n  <div class="card">Hover to lift!</div>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { padding: 40px; background: #1a1a2e; }\n    .btn {\n      padding: 15px 30px;\n      background: #4a9eff;\n      color: white;\n      border: none;\n      border-radius: 8px;\n      cursor: pointer;\n      font-size: 16px;\n      /* Add transition here */\n    }\n    .btn:hover {\n      /* Add transform here */\n    }\n  </style>\n</head>\n<body>\n  <button class="btn">Hover Me!</button>\n</body>\n</html>`,
    validationRules: [
      { type:'contains_text', value:'transition', message:'Add a transition property to the element' },
      { type:'contains_text', value:'transform',  message:'Add a transform on the :hover state' },
    ],
  },

  c6: {
    title: 'Media Queries',
    xpReward: 130, coinsReward: 55,
    instructions: `
<div class="mi-track">🎨 CSS — Mission 6 of 6</div>
<h2 class="mi-title">Responsive Design with Media Queries</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE MEDIA QUERIES?</div>
  <p>Over 60% of web traffic comes from mobile devices. <strong>Media queries</strong> let you write 
  different CSS rules that only apply at certain screen sizes — making your site look 
  great on phones, tablets, and desktops.</p>
  <p>This is called <strong>Responsive Web Design</strong>.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Add a media query that changes the layout for small screens:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Write a @media rule targeting small screens</strong><br>
      <pre>@media (max-width: 600px) {\n  /* CSS rules here only apply on screens 600px or narrower */\n}</pre>
      <span class="mi-note">600px is a common mobile breakpoint. Also common: 768px (tablet), 1024px (small desktop).</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Change font-size inside the query</strong><br>
      <pre>@media (max-width: 600px) {\n  body {\n    font-size: 14px;  /* smaller text on phones */\n  }\n}</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
          <strong>Optional: change layout for mobile</strong><br>
      <pre>@media (max-width: 600px) {\n  .container {\n    flex-direction: column;  /* stack vertically on mobile */\n  }\n}</pre>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ MEDIA QUERY SYNTAX</div>
  <pre>@media (condition) {
  /* CSS rules applied when condition is true */
  selector {
    property: value;
  }
}

Common conditions:
@media (max-width: 600px)   /* smaller than 600px */
@media (min-width: 768px)   /* larger than 768px */
@media (orientation: portrait)  /* portrait mode */</pre>
</div>

<div class="mi-compare">
  <div class="mi-section-label">🔍 COMMON BREAKPOINTS</div>
  <table class="mi-table">
    <tr><th>Breakpoint</th><th>Target Device</th></tr>
    <tr><td>max-width: 480px</td><td>Small phones</td></tr>
    <tr><td>max-width: 768px</td><td>Phones &amp; tablets</td></tr>
    <tr><td>max-width: 1024px</td><td>Tablets &amp; small laptops</td></tr>
    <tr><td>min-width: 1200px</td><td>Large desktops</td></tr>
  </table>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>@media</code> rule with a max-width or min-width condition</li>
    <li>☐ <code>font-size</code> changed inside the media query</li>
    <li>☐ Resize the Live Preview window to see your responsive layout!</li>
  </ul>
</div>`,
    hint: `💡 Add the @media rule at the BOTTOM of your <style> block:

<style>
  /* Default styles (for all screens) */
  body {
    font-size: 18px;
    background: #1a1a2e;
    color: white;
  }

  /* Mobile styles (only when screen is 600px or less) */
  @media (max-width: 600px) {
    body {
      font-size: 14px;
    }
  }
</style>`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <title>Responsive Page</title>\n  <style>\n    body {\n      font-size: 18px;\n      font-family: Arial, sans-serif;\n      background: #1a1a2e;\n      color: white;\n      padding: 20px;\n    }\n    h1 { color: #00f5ff; font-size: 32px; }\n    .layout {\n      display: flex;\n      gap: 20px;\n      margin-top: 20px;\n    }\n    .main {\n      flex: 3;\n      background: #2a2a4e;\n      padding: 20px;\n      border-radius: 8px;\n    }\n    .sidebar {\n      flex: 1;\n      background: #3a2a4e;\n      padding: 20px;\n      border-radius: 8px;\n    }\n    /* Mobile: stack vertically */\n    @media (max-width: 600px) {\n      body { font-size: 14px; }\n      h1 { font-size: 22px; }\n      .layout { flex-direction: column; }\n    }\n  </style>\n</head>\n<body>\n  <h1>Responsive Layout</h1>\n  <div class="layout">\n    <div class="main">\n      <h2>Main Content</h2>\n      <p>On large screens this is a wide column. On mobile it stacks vertically.</p>\n    </div>\n    <div class="sidebar">\n      <h2>Sidebar</h2>\n      <p>This moves below the main content on small screens.</p>\n    </div>\n  </div>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head>\n  <title>Responsive</title>\n  <style>\n    body {\n      font-size: 18px;\n      background: #1a1a2e;\n      color: white;\n      padding: 20px;\n    }\n    /* Add your @media query below */\n\n  </style>\n</head>\n<body>\n  <h1>Resize the Browser!</h1>\n  <p>The font size should change on small screens.</p>\n</body>\n</html>`,
    validationRules: [
      { type:'contains_text', value:'@media',    message:'Add a @media query for different screen sizes' },
      { type:'contains_text', value:'font-size', message:'Change the font-size inside the media query' },
    ],
  },

  /* ════════════════════════════════════════
     JAVASCRIPT TRACK  (j1 – j8)
  ════════════════════════════════════════ */

  j1: {
    title: 'Variables & Data Types',
    xpReward: 70, coinsReward: 30,
    instructions: `
<div class="mi-track">⚡ JavaScript — Mission 1 of 8</div>
<h2 class="mi-title">Variables & Data Types</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE VARIABLES?</div>
  <p>Variables are like labelled containers that store data. You give the container a name, 
  put something inside, and then reference it by name later in your code.</p>
  <p>JavaScript has three ways to declare variables: <code>var</code> (old, avoid it), 
  <code>let</code> (modern, can change), and <code>const</code> (modern, cannot change).</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Declare variables and print them to the console:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Declare a const variable (cannot be reassigned)</strong><br>
      Use for values that should never change.
      <pre>const playerName = "Alice";\nconst MAX_LEVEL = 100;\nconst PI = 3.14159;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Declare a let variable (can be changed)</strong><br>
      Use for values that will change over time.
      <pre>let score = 0;\nlet isPlaying = true;\nlet currentLevel = 1;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Use console.log() to print the values</strong><br>
      <code>console.log()</code> prints to the browser console (press F12 to open it).
      <pre>console.log(playerName);          // prints: Alice\nconsole.log("Score:", score);     // prints: Score: 0\nconsole.log(typeof playerName);   // prints: string</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 JAVASCRIPT DATA TYPES</div>
  <table class="mi-table">
    <tr><th>Type</th><th>Example</th><th>Use for</th></tr>
    <tr><td>string</td><td>"Hello" or 'Hello'</td><td>Text</td></tr>
    <tr><td>number</td><td>42, 3.14, -10</td><td>Numbers (int & float)</td></tr>
    <tr><td>boolean</td><td>true or false</td><td>Yes/No values</td></tr>
    <tr><td>array</td><td>["a", "b", "c"]</td><td>List of values</td></tr>
    <tr><td>object</td><td>{ name: "Alice" }</td><td>Grouped properties</td></tr>
    <tr><td>null</td><td>null</td><td>Intentionally empty</td></tr>
    <tr><td>undefined</td><td>undefined</td><td>Not yet assigned</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>💡 How to open the console:</strong> Press <strong>F12</strong> (or right-click → Inspect) 
  in your browser, then click the "Console" tab. That's where console.log() output appears.
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ At least one <code>const</code> variable declared and given a value</li>
    <li>☐ At least one <code>let</code> variable declared and given a value</li>
    <li>☐ <code>console.log()</code> called to print at least one variable</li>
  </ul>
</div>`,
    hint: `💡 Think of variables as labelled boxes:
const = sealed box (can't change what's inside)
let   = open box  (can change the contents)

const name = "Alice";   // sealed — name is always Alice
let score = 0;          // open   — score can change to 10, 50, etc.
score = 50;             // this is fine for let
// name = "Bob";        // this would cause an ERROR for const

console.log(name, score);  // prints: Alice 50`,
    solution: `// ── const: for values that never change ──────────────────────\nconst playerName = "CyberNinja";\nconst MAX_HEALTH = 100;\nconst GAME_VERSION = "1.0.0";\n\n// ── let: for values that change during the game ───────────────\nlet currentHealth = 100;\nlet score = 0;\nlet level = 1;\nlet isAlive = true;\n\n// ── Different data types ───────────────────────────────────────\nconst greeting = "Hello, World!";  // string\nconst age = 25;                    // number\nconst pi = 3.14159;                // number (decimal)\nconst isLoggedIn = true;           // boolean\nconst nothing = null;              // null\n\n// ── Printing to console ───────────────────────────────────────\nconsole.log("Player:", playerName);\nconsole.log("Max Health:", MAX_HEALTH);\nconsole.log("Current Health:", currentHealth);\nconsole.log("Score:", score);\nconsole.log("Level:", level);\n\n// ── typeof tells you the data type ────────────────────────────\nconsole.log(typeof greeting);    // "string"\nconsole.log(typeof age);         // "number"\nconsole.log(typeof isLoggedIn);  // "boolean"`,
    starterCode: `// Declare your variables here\n\n// Use const for things that don't change:\n\n// Use let for things that can change:\n\n// Print them with console.log()\n`,
    validationRules: [
      { type:'contains_text', value:'const',       message:'Declare at least one const variable' },
      { type:'contains_text', value:'let',         message:'Declare at least one let variable' },
      { type:'contains_text', value:'console.log', message:'Use console.log() to print a value' },
    ],
  },

  j2: {
    title: 'Functions',
    xpReward: 80, coinsReward: 35,
    instructions: `
<div class="mi-track">⚡ JavaScript — Mission 2 of 8</div>
<h2 class="mi-title">Functions</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE FUNCTIONS?</div>
  <p>A function is a <strong>reusable block of code</strong> that performs a specific task. 
  Instead of writing the same code 10 times, you write it once in a function and call it 
  whenever you need it. Functions are one of the most fundamental concepts in programming.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Write a greet function and call it:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Define the function with the function keyword</strong><br>
      <pre>function greet(name) {\n  // code goes here\n}</pre>
      <span class="mi-note">The word inside the brackets is a parameter — a variable that receives the value you pass in when calling the function.</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add a return statement</strong><br>
      <code>return</code> sends a value back to wherever the function was called from.
      <pre>function greet(name) {\n  return "Hello, " + name + "! Welcome to Codrion!";\n}</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Call the function and log the result</strong><br>
      <pre>const message = greet("Alice");\nconsole.log(message);  // Hello, Alice! Welcome to Codrion!\n\n// Or in one line:\nconsole.log(greet("Bob"));</pre>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ FUNCTION ANATOMY</div>
  <pre>function greet(name) {
   ↑        ↑   ↑
   keyword  name parameter(s)

  return "Hello, " + name;
  ↑      ↑
  keyword value sent back

}

greet("Alice");
↑      ↑
name   argument (value passed in)</pre>
</div>

<div class="mi-compare">
  <div class="mi-section-label">🔍 PARAMETERS vs ARGUMENTS</div>
  <table class="mi-table">
    <tr><th></th><th>Definition</th><th>Example</th></tr>
    <tr><td>Parameter</td><td>Variable in the function definition</td><td>function greet(<strong>name</strong>)</td></tr>
    <tr><td>Argument</td><td>Actual value passed when calling</td><td>greet(<strong>"Alice"</strong>)</td></tr>
  </table>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ Function defined using the <code>function</code> keyword</li>
    <li>☐ Function has at least one parameter</li>
    <li>☐ <code>return</code> statement sending back a value</li>
    <li>☐ Function called at least once with <code>console.log()</code></li>
  </ul>
</div>`,
    hint: `💡 Full function example:

// DEFINE the function (this doesn't run yet)
function greet(name) {
  return "Hello, " + name + "!";
}

// CALL the function (this runs it)
console.log(greet("Alice"));  // Hello, Alice!
console.log(greet("Bob"));    // Hello, Bob!

// You can also store the result:
const message = greet("Charlie");
console.log(message);  // Hello, Charlie!`,
    solution: `// Basic greeting function\nfunction greet(name) {\n  return "Hello, " + name + "! Welcome to Codrion!";\n}\n\n// Function with multiple parameters\nfunction add(a, b) {\n  return a + b;\n}\n\n// Function with a condition\nfunction getGrade(score) {\n  if (score >= 90) return "A";\n  if (score >= 80) return "B";\n  if (score >= 70) return "C";\n  return "F";\n}\n\n// Function that returns a formatted string\nfunction celebrate(name, score) {\n  const grade = getGrade(score);\n  return name + " scored " + score + " — Grade: " + grade;\n}\n\n// Call the functions\nconsole.log(greet("Alice"));\nconsole.log(greet("Bob"));\nconsole.log(add(5, 3));\nconsole.log(add(100, 200));\nconsole.log(celebrate("Alice", 95));\nconsole.log(celebrate("Bob", 73));`,
    starterCode: `// Write your greet function\nfunction greet(name) {\n  // Return a greeting that includes the name\n}\n\n// Call it and log the result\nconsole.log(greet("World"));`,
    validationRules: [
      { type:'contains_text', value:'function', message:'Define a function using the function keyword' },
      { type:'contains_text', value:'return',   message:'Use return to send back a value' },
    ],
  },

  j3: {
    title: 'Arrays & Loops',
    xpReward: 90, coinsReward: 40,
    instructions: `
<div class="mi-track">⚡ JavaScript — Mission 3 of 8</div>
<h2 class="mi-title">Arrays & Loops</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE ARRAYS AND LOOPS?</div>
  <p>An <strong>array</strong> is an ordered list that can hold multiple values in a single variable. 
  Think of it like a numbered shelf — each item has a position (index) starting at 0.</p>
  <p>A <strong>loop</strong> repeats a block of code multiple times — for example, once for each 
  item in an array. Without loops, you would have to write the same code 100 times for a 
  list of 100 items!</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Create an array and iterate through it with a loop:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Create an array with at least 3 items</strong><br>
      Arrays use square brackets. Items are separated by commas.
      <pre>const fruits = ["apple", "banana", "cherry"];\nconst numbers = [10, 20, 30, 40, 50];\nconst mixed = ["hello", 42, true, null];</pre>
      <span class="mi-note">Access items by index: fruits[0] = "apple", fruits[1] = "banana"</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Use a for loop to iterate through it</strong><br>
      <pre>for (let i = 0; i &lt; fruits.length; i++) {\n  console.log(fruits[i]);\n}</pre>
      <span class="mi-note">fruits.length gives you the number of items (3). The loop runs while i is less than 3, so i goes: 0, 1, 2.</span>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ FOR LOOP ANATOMY</div>
  <pre>for (let i = 0; i &lt; array.length; i++) {
       ↑           ↑                ↑
       start at 0  run while true   increase i by 1 each time
  
  console.log(array[i]);  ← access item at current position
}</pre>
</div>

<div class="mi-compare">
  <div class="mi-section-label">🔍 THREE WAYS TO LOOP</div>
  <table class="mi-table">
    <tr><th>Loop type</th><th>Syntax</th><th>Best when</th></tr>
    <tr><td>for</td><td>for (let i=0; i&lt;n; i++)</td><td>You need the index i</td></tr>
    <tr><td>for...of</td><td>for (const item of array)</td><td>You just need each item</td></tr>
    <tr><td>.forEach()</td><td>array.forEach(item => {})</td><td>Functional style</td></tr>
  </table>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ Array declared with at least 3 items inside <code>[]</code></li>
    <li>☐ <code>for</code> loop that iterates through the array</li>
    <li>☐ Each item logged or used inside the loop</li>
  </ul>
</div>`,
    hint: `💡 Array + for loop pattern:

const fruits = ["apple", "banana", "cherry"];
//                 [0]       [1]       [2]

for (let i = 0; i < fruits.length; i++) {
  console.log(i, fruits[i]);
}
// Output:
// 0 "apple"
// 1 "banana"
// 2 "cherry"

Or modern for...of:
for (const fruit of fruits) {
  console.log(fruit);
}`,
    solution: `// Create arrays\nconst fruits = ["apple", "banana", "cherry", "mango", "kiwi"];\nconst scores = [95, 87, 72, 100, 65];\n\n// Classic for loop (access by index)\nconsole.log("=== Fruits List ===");\nfor (let i = 0; i < fruits.length; i++) {\n  console.log(i + 1 + ". " + fruits[i]);\n}\n\n// for...of loop (simpler, no index)\nconsole.log("\\n=== Scores ===");\nfor (const score of scores) {\n  const grade = score >= 90 ? "A" : score >= 70 ? "B" : "C";\n  console.log("Score: " + score + " → Grade: " + grade);\n}\n\n// Array methods (bonus)\nconsole.log("\\nTotal scores:", scores.reduce((sum, n) => sum + n, 0));\nconsole.log("Highest:", Math.max(...scores));\nconsole.log("Average:", scores.reduce((s,n) => s+n, 0) / scores.length);`,
    starterCode: `// Create an array with at least 3 items\nconst fruits = [];\n\n// Use a for loop to log each item\n`,
    validationRules: [
      { type:'regex',         value:'\\[.+\\]', message:'Create an array with items inside []' },
      { type:'contains_text', value:'for',      message:'Use a for loop to iterate through the array' },
    ],
  },

  j4: {
    title: 'Objects',
    xpReward: 90, coinsReward: 40,
    instructions: `
<div class="mi-track">⚡ JavaScript — Mission 4 of 8</div>
<h2 class="mi-title">Objects</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE OBJECTS?</div>
  <p>Objects store related data together as <strong>key-value pairs</strong>, where each key 
  is a property name and each value is the data stored for that property. Think of an object 
  like a real-world entity: a person has a name, age, and job. An object lets you keep all 
  those properties bundled together.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Create an object with properties and access them:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
          <strong>Create an object with at least 3 properties</strong><br>
      Objects use curly braces. Each property is <code>key: value</code> separated by commas.
      <pre>const person = {\n  name: "Alice",\n  age: 25,\n  city: "London",\n  isStudent: true\n};</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Access properties using dot notation</strong><br>
      <pre>console.log(person.name);  // Alice\nconsole.log(person.age);   // 25</pre>
      <span class="mi-note">You can also use bracket notation: person["name"] — useful when the key is stored in a variable.</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Add or update properties</strong><br>
      <pre>person.email = "alice@email.com";  // add new\nperson.age = 26;                   // update existing</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 ARRAYS vs OBJECTS</div>
  <table class="mi-table">
    <tr><th></th><th>Array</th><th>Object</th></tr>
    <tr><td>Syntax</td><td>[ ]</td><td>{ }</td></tr>
    <tr><td>Access by</td><td>Index: items[0]</td><td>Key: person.name</td></tr>
    <tr><td>Best for</td><td>Ordered list of same thing</td><td>Properties of one thing</td></tr>
    <tr><td>Example</td><td>["Alice","Bob","Charlie"]</td><td>{name:"Alice", age:25}</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>💡 Objects can contain arrays, and arrays can contain objects!</strong><br>
  const player = { name: "Alice", scores: [95, 87, 100] }
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ Object created with curly braces <code>{ }</code></li>
    <li>☐ At least 3 key-value pairs inside the object</li>
    <li>☐ At least one property accessed and logged</li>
  </ul>
</div>`,
    hint: `💡 Object syntax and access:

const player = {
  name: "Alice",        // string value
  level: 5,            // number value
  isOnline: true,      // boolean value
  badges: ["HTML", "CSS"]  // array value
};

// Access with dot notation
console.log(player.name);    // Alice
console.log(player.level);   // 5
console.log(player.badges);  // ["HTML", "CSS"]

// Log the whole object
console.log(player);`,
    solution: `// Create a player profile object\nconst player = {\n  name: "NeonCoder",\n  level: 7,\n  xp: 3450,\n  coins: 1200,\n  isOnline: true,\n  badges: ["HTML Beginner", "CSS Beginner", "JS Beginner"],\n  stats: {\n    missionsCompleted: 15,\n    streak: 12\n  }\n};\n\n// Access properties\nconsole.log("Name:", player.name);\nconsole.log("Level:", player.level);\nconsole.log("XP:", player.xp);\nconsole.log("Badges:", player.badges);\nconsole.log("Streak:", player.stats.streak);  // nested object\n\n// Add new property\nplayer.rank = "Gold";\nconsole.log("Rank:", player.rank);\n\n// Update existing property\nplayer.level = 8;\nplayer.xp += 500;\nconsole.log("New Level:", player.level);\nconsole.log("New XP:", player.xp);\n\n// Log the whole object\nconsole.log("Full player data:", player);`,
    starterCode: `// Create a person or player object\nconst player = {\n  // Add at least 3 properties here\n};\n\n// Access and log properties\nconsole.log(player);`,
    validationRules: [
      { type:'regex',         value:'\\{[\\s\\S]+\\}', message:'Create an object with properties inside {}' },
      { type:'contains_text', value:'console.log',    message:'Log the object or its properties' },
    ],
  },

  j5: {
    title: 'DOM Manipulation',
    xpReward: 110, coinsReward: 45,
    badgeReward: { id:'js_beginner', name:'JS Beginner', icon:'⚡' },
    instructions: `
<div class="mi-track">⚡ JavaScript — Mission 5 of 8  ★ BADGE MISSION</div>
<h2 class="mi-title">DOM Manipulation</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT IS THE DOM?</div>
  <p>The <strong>DOM (Document Object Model)</strong> is the live, interactive representation 
  of your HTML in the browser's memory. JavaScript can read and change the DOM in real time — 
  that's how interactive websites work! When you click a button and something changes on screen 
  without reloading the page, that's DOM manipulation.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Use JavaScript to select an HTML element and change its content:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
          <strong>Add an id attribute to an HTML element</strong><br>
      The id is the "address" JavaScript uses to find the element.
      <pre>&lt;h1 id="title"&gt;Original Title&lt;/h1&gt;</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Select the element with getElementById()</strong><br>
      <pre>const element = document.getElementById("title");</pre>
      <span class="mi-note">document = the whole page. getElementById() searches it for an element with that exact id.</span>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Change the element's innerHTML</strong><br>
      <code>innerHTML</code> sets the HTML content inside the element.
      <pre>element.innerHTML = "New Title! 🎉";\n// Or add HTML tags:\nelement.innerHTML = "&lt;em&gt;Italicised Title&lt;/em&gt;";</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">4</span>
    <div>
      <strong>Optional: change style too</strong><br>
      <pre>element.style.color = "gold";\nelement.style.fontSize = "32px";\nelement.style.backgroundColor = "#1a1a2e";</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 WAYS TO SELECT ELEMENTS</div>
  <table class="mi-table">
    <tr><th>Method</th><th>Selects</th><th>Example</th></tr>
    <tr><td>getElementById</td><td>One element by id</td><td>document.getElementById("title")</td></tr>
    <tr><td>querySelector</td><td>First match by CSS selector</td><td>document.querySelector(".btn")</td></tr>
    <tr><td>querySelectorAll</td><td>All matches</td><td>document.querySelectorAll("p")</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>🏅 Badge reward!</strong> Complete this mission to earn the <strong>JS Beginner</strong> badge!
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ HTML element with an <code>id</code> attribute</li>
    <li>☐ <code>document.getElementById("id")</code> to select it</li>
    <li>☐ <code>.innerHTML</code> changed to new content</li>
    <li>☐ Check the Live Preview tab to see the change!</li>
  </ul>
</div>`,
    hint: `💡 Three steps: add id → select → change

HTML:
<h1 id="myTitle">Original</h1>
<button onclick="changeIt()">Click Me</button>

JavaScript (in <script> tags):
function changeIt() {
  const el = document.getElementById("myTitle");
  el.innerHTML = "Changed by JavaScript! 🎉";
  el.style.color = "gold";
}`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { font-family: Arial; background: #1a1a2e; color: white; padding: 30px; text-align: center; }\n    button { padding: 12px 24px; background: #4a9eff; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 15px; margin: 5px; }\n    button:hover { background: #0070ff; }\n    #message { font-size: 28px; font-weight: bold; margin: 30px 0; min-height: 40px; }\n    #colourBox { width: 150px; height: 150px; background: grey; margin: 20px auto; border-radius: 8px; }\n  </style>\n</head>\n<body>\n  <h1>DOM Manipulation Demo</h1>\n  <p id="message">Click a button below!</p>\n  <div id="colourBox"></div>\n  <button onclick="changeText()">Change Text</button>\n  <button onclick="changeColor()">Change Colour</button>\n  <button onclick="resetAll()">Reset</button>\n  <script>\n    function changeText() {\n      const el = document.getElementById("message");\n      el.innerHTML = "You changed the text! 🎉";\n      el.style.color = "#ffd700";\n    }\n    function changeColor() {\n      const box = document.getElementById("colourBox");\n      const colors = ["#ff6432", "#4a9eff", "#39ff14", "#7c3aed", "#ffd700"];\n      const random = colors[Math.floor(Math.random() * colors.length)];\n      box.style.backgroundColor = random;\n    }\n    function resetAll() {\n      document.getElementById("message").innerHTML = "Click a button below!";\n      document.getElementById("message").style.color = "white";\n      document.getElementById("colourBox").style.backgroundColor = "grey";\n    }\n  </script>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { font-family: Arial; padding: 20px; }\n    #title { color: #333; font-size: 24px; }\n  </style>\n</head>\n<body>\n  <h1 id="title">Original Title</h1>\n  <button onclick="changeIt()">Click to Change!</button>\n  <script>\n    function changeIt() {\n      // Select the element with id="title"\n      const el = document.getElementById("title");\n      // Change its innerHTML to something new\n    }\n  </script>\n</body>\n</html>`,
    validationRules: [
      { type:'contains_text', value:'getElementById', message:'Use document.getElementById() to select an element' },
      { type:'contains_text', value:'innerHTML',      message:'Change the .innerHTML property' },
    ],
  },

  j6: {
    title: 'Event Listeners',
    xpReward: 120, coinsReward: 50,
    instructions: `
<div class="mi-track">⚡ JavaScript — Mission 6 of 8</div>
<h2 class="mi-title">Event Listeners</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE EVENT LISTENERS?</div>
  <p>Event listeners make your page <strong>interactive</strong>. They "listen" for something 
  to happen (a user clicks, types, moves the mouse) and then run a function in response. 
  Without events, a webpage is static. With events, it becomes an app!</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Make a button interactive using addEventListener:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Select the button element</strong><br>
      <pre>const btn = document.getElementById("myButton");</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Add a click event listener</strong><br>
      <code>addEventListener</code> takes two arguments: the event name, and the function to run.
      <pre>btn.addEventListener("click", function() {\n  // This code runs every time the button is clicked\n  console.log("Button was clicked!");\n});</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Change something visible on the page</strong><br>
      <pre>btn.addEventListener("click", function() {\n  const output = document.getElementById("result");\n  output.innerHTML = "You clicked the button! 🎉";\n  output.style.color = "gold";\n});</pre>
    </div>
  </li>
</ol>

<div class="mi-compare">
  <div class="mi-section-label">🔍 COMMON EVENT TYPES</div>
  <table class="mi-table">
    <tr><th>Event</th><th>Triggered when</th></tr>
    <tr><td>"click"</td><td>User clicks an element</td></tr>
    <tr><td>"mouseover"</td><td>Mouse moves over an element</td></tr>
    <tr><td>"keydown"</td><td>User presses a key</td></tr>
    <tr><td>"input"</td><td>User types in an input field</td></tr>
    <tr><td>"submit"</td><td>Form is submitted</td></tr>
    <tr><td>"load"</td><td>Page finishes loading</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>💡 addEventListener vs onclick:</strong><br>
  <code>btn.onclick = fn</code> — only one handler (overwrites previous)<br>
  <code>btn.addEventListener("click", fn)</code> — can add multiple handlers, recommended!
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ Button selected with <code>getElementById</code></li>
    <li>☐ <code>addEventListener("click", ...)</code> attached to it</li>
    <li>☐ Something visible changes when the button is clicked</li>
    <li>☐ Test it in the Live Preview tab!</li>
  </ul>
</div>`,
    hint: `💡 The full addEventListener pattern:

const btn = document.getElementById("btn");

btn.addEventListener("click", function() {
  // Code here runs when button is clicked
  document.getElementById("output").innerHTML = "Clicked!";
});

// Arrow function version (same thing):
btn.addEventListener("click", () => {
  document.getElementById("output").innerHTML = "Clicked!";
});`,
    solution: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { font-family: Arial; background: #1a1a2e; color: white; padding: 30px; }\n    button { padding: 12px 24px; background: #4a9eff; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 16px; }\n    button:hover { background: #0070ff; }\n    #counter-display { font-size: 64px; font-weight: bold; color: #ffd700; text-align: center; margin: 20px 0; }\n    #status { text-align: center; color: #39ff14; font-size: 16px; margin-top: 10px; }\n  </style>\n</head>\n<body>\n  <h1>Click Counter</h1>\n  <p id="counter-display">0</p>\n  <div style="text-align:center">\n    <button id="increaseBtn">+ Click Me!</button>\n    <button id="resetBtn" style="background:#ff6432;margin-left:10px">Reset</button>\n  </div>\n  <p id="status">Click the button!</p>\n  <script>\n    let count = 0;\n    const display = document.getElementById("counter-display");\n    const status  = document.getElementById("status");\n\n    document.getElementById("increaseBtn").addEventListener("click", function() {\n      count++;\n      display.innerHTML = count;\n      if (count === 10) {\n        status.innerHTML = "🎉 You reached 10! Amazing!";\n        display.style.color = "#00f5ff";\n      } else if (count >= 5) {\n        status.innerHTML = (10 - count) + " more to reach 10!";\n      } else {\n        status.innerHTML = "Keep clicking!";\n      }\n    });\n\n    document.getElementById("resetBtn").addEventListener("click", function() {\n      count = 0;\n      display.innerHTML = "0";\n      display.style.color = "#ffd700";\n      status.innerHTML = "Reset! Click the button!";\n    });\n  </script>\n</body>\n</html>`,
    starterCode: `<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { font-family: Arial; padding: 20px; }\n  </style>\n</head>\n<body>\n  <button id="btn">Click Me!</button>\n  <p id="output">Waiting for a click...</p>\n  <script>\n    const btn = document.getElementById("btn");\n    // Add a click event listener here\n\n  </script>\n</body>\n</html>`,
    validationRules: [
      { type:'contains_text', value:'addEventListener', message:'Use addEventListener to attach an event' },
      { type:'contains_text', value:'click',            message:'Listen for the "click" event' },
    ],
  },

  j7: {
    title: 'Arrow Functions',
    xpReward: 130, coinsReward: 55,
    instructions: `
<div class="mi-track">⚡ JavaScript — Mission 7 of 8</div>
<h2 class="mi-title">Arrow Functions</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT ARE ARROW FUNCTIONS?</div>
  <p>Arrow functions are a <strong>shorter, more modern syntax</strong> for writing functions 
  introduced in ES6 (2015). They use the <code>=&gt;</code> symbol (hence "arrow"). 
  You'll see arrow functions everywhere in modern JavaScript, especially with array methods 
  like <code>.map()</code>, <code>.filter()</code>, and <code>.reduce()</code>.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Write an arrow function and use it with .map():</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Convert a regular function to an arrow function</strong><br>
      <pre>// Regular function\nfunction double(n) {\n  return n * 2;\n}\n\n// Arrow function — same thing, shorter!\nconst double = n =&gt; n * 2;\n\n// With multiple parameters (need brackets):\nconst add = (a, b) =&gt; a + b;\n\n// With a code block (need curly braces and return):\nconst greet = name =&gt; {\n  const msg = "Hello, " + name + "!";\n  return msg;\n};</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Use .map() to transform an array</strong><br>
      <code>.map()</code> creates a NEW array by running a function on every item.
      <pre>const numbers = [1, 2, 3, 4, 5];\n\n// Double every number\nconst doubled = numbers.map(n =&gt; n * 2);\nconsole.log(doubled); // [2, 4, 6, 8, 10]\n\n// Get the length of each word\nconst words = ["apple", "hi", "banana"];\nconst lengths = words.map(w =&gt; w.length);\nconsole.log(lengths); // [5, 2, 6]</pre>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ ARROW FUNCTION SYNTAX FORMS</div>
  <pre>// 1 param, single expression:
const double = n => n * 2;

// Multiple params:
const add = (a, b) => a + b;

// No params:
const greet = () => "Hello!";

// Multi-line body:
const describe = (name, age) => {
  return name + " is " + age + " years old";
};</pre>
</div>

<div class="mi-compare">
  <div class="mi-section-label">🔍 MAP vs FILTER vs REDUCE</div>
  <table class="mi-table">
    <tr><th>Method</th><th>What it does</th><th>Returns</th></tr>
    <tr><td>.map(fn)</td><td>Transform every item</td><td>New array, same length</td></tr>
    <tr><td>.filter(fn)</td><td>Keep items where fn returns true</td><td>New array, shorter/same</td></tr>
    <tr><td>.reduce(fn)</td><td>Combine all items into one value</td><td>Single value</td></tr>
  </table>
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ Arrow function written using <code>=&gt;</code> syntax</li>
    <li>☐ Array method <code>.map()</code> called on an array</li>
    <li>☐ Result logged with <code>console.log()</code></li>
  </ul>
</div>`,
    hint: `💡 Arrow functions make .map() very clean:

const numbers = [1, 2, 3, 4, 5];

// Regular function with .map():
const doubled = numbers.map(function(n) { return n * 2; });

// Arrow function with .map() — much shorter:
const doubled = numbers.map(n => n * 2);

console.log(doubled); // [2, 4, 6, 8, 10]`,
    solution: `// ── Arrow function syntax forms ───────────────────────────────\nconst double = n => n * 2;\nconst square = n => n * n;\nconst greet = name => "Hello, " + name + "!";\nconst add = (a, b) => a + b;\nconst isEven = n => n % 2 === 0;\n\n// ── Using .map() — transform every item ────────────────────────\nconst numbers = [1, 2, 3, 4, 5];\n\nconst doubled  = numbers.map(n => n * 2);\nconst squared  = numbers.map(n => n * n);\nconst asString = numbers.map(n => "Item " + n);\n\nconsole.log("Doubled:", doubled);   // [2, 4, 6, 8, 10]\nconsole.log("Squared:", squared);   // [1, 4, 9, 16, 25]\nconsole.log("As text:", asString);  // ["Item 1", ...]\n\n// ── Using .filter() — keep only matching items ─────────────────\nconst evens = numbers.filter(n => isEven(n));\nconst odds  = numbers.filter(n => n % 2 !== 0);\nconst big   = numbers.filter(n => n > 3);\n\nconsole.log("Evens:", evens);  // [2, 4]\nconsole.log("Odds:", odds);    // [1, 3, 5]\nconsole.log("Big:", big);      // [4, 5]\n\n// ── Using .reduce() — combine all into one ─────────────────────\nconst sum     = numbers.reduce((total, n) => total + n, 0);\nconst product = numbers.reduce((total, n) => total * n, 1);\n\nconsole.log("Sum:", sum);         // 15\nconsole.log("Product:", product); // 120`,
    starterCode: `// Convert this regular function to an arrow function:\nfunction double(n) {\n  return n * 2;\n}\n\n// Your arrow function:\n// const double = \n\nconst numbers = [1, 2, 3, 4, 5];\n// Use .map() with your arrow function:\nconsole.log(numbers.map(double));`,
    validationRules: [
      { type:'regex',         value:'=>',     message:'Use the arrow function syntax =>' },
      { type:'contains_text', value:'.map(',  message:'Use the .map() array method' },
    ],
  },

  j8: {
    title: 'Promises & Async/Await',
    xpReward: 150, coinsReward: 60,
    instructions: `
<div class="mi-track">⚡ JavaScript — Mission 8 of 8  ★ FINAL MISSION</div>
<h2 class="mi-title">Promises & Async/Await</h2>

<div class="mi-what">
  <div class="mi-section-label">📖 WHAT IS ASYNCHRONOUS JAVASCRIPT?</div>
  <p>JavaScript normally runs code <strong>line by line</strong>. But some operations take time — 
  fetching data from a server, reading a file, waiting for a timer. If JS waited for each one 
  to finish before moving on, the whole page would freeze!</p>
  <p><strong>Promises</strong> and <strong>async/await</strong> let JavaScript start a slow task, 
  continue doing other things, and then "come back" when the task is done — without freezing the page.</p>
</div>

<div class="mi-section-label">🎯 YOUR TASK</div>
<p>Create a Promise that resolves with a value and handle it:</p>

<ol class="mi-steps">
  <li>
    <span class="mi-step-num">1</span>
    <div>
      <strong>Create a new Promise</strong><br>
      A Promise wraps an async operation. It gives you <code>resolve</code> (success) 
      and <code>reject</code> (failure) functions.
      <pre>const myPromise = new Promise((resolve, reject) => {\n  // Do your async work here\n  // Call resolve() when done successfully\n  // Call reject() if something went wrong\n});</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">2</span>
    <div>
      <strong>Call resolve() with a value</strong><br>
      <pre>const myPromise = new Promise((resolve, reject) => {\n  resolve("Mission accomplished! 🎉");\n});</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">3</span>
    <div>
      <strong>Handle the result with .then() and .catch()</strong><br>
      <pre>myPromise\n  .then(result => console.log("Success:", result))\n  .catch(error => console.log("Error:", error));</pre>
    </div>
  </li>
  <li>
    <span class="mi-step-num">4</span>
    <div>
      <strong>Modern syntax: async/await</strong><br>
      <pre>async function getData() {\n  try {\n    const result = await myPromise;\n    console.log(result);\n  } catch (err) {\n    console.error(err);\n  }\n}\ngetData();</pre>
    </div>
  </li>
</ol>

<div class="mi-structure">
  <div class="mi-section-label">🏗️ PROMISE STATES</div>
  <pre>Promise States:
  ┌─────────────┐
  │   PENDING   │  ← waiting (initial state)
  └──────┬──────┘
         │
    ┌────┴────┐
    ▼         ▼
┌─────────┐  ┌──────────┐
│FULFILLED│  │ REJECTED │
│(success)│  │(failure) │
└─────────┘  └──────────┘
     ↓              ↓
  .then()         .catch()</pre>
</div>

<div class="mi-compare">
  <div class="mi-section-label">🔍 .THEN() vs ASYNC/AWAIT</div>
  <table class="mi-table">
    <tr><th></th><th>.then() style</th><th>async/await style</th></tr>
    <tr><td>Syntax</td><td>Chained .then() calls</td><td>Looks like synchronous code</td></tr>
    <tr><td>Error handling</td><td>.catch()</td><td>try/catch block</td></tr>
    <tr><td>Readability</td><td>Gets complex when chaining</td><td>Cleaner and easier to read</td></tr>
    <tr><td>Both do</td><td colspan="2">The exact same thing!</td></tr>
  </table>
</div>

<div class="mi-note-box">
  <strong>🏆 Final Mission!</strong> Completing this unlocks the final JS mission badge. 
  Promises are used everywhere in modern web development — every API call uses them!
</div>

<div class="mi-check">
  <div class="mi-section-label">✅ CHECKLIST</div>
  <ul>
    <li>☐ <code>new Promise((resolve, reject) => {})</code> created</li>
    <li>☐ <code>resolve()</code> called with a value inside the Promise</li>
    <li>☐ <code>.then()</code> used to handle the resolved value</li>
  </ul>
</div>`,
    hint: `💡 Minimal working example:

// Create
const myPromise = new Promise((resolve, reject) => {
  resolve("Hello from the future!");  // call resolve
});

// Handle
myPromise
  .then(value => console.log(value))   // Success!
  .catch(error => console.log(error)); // Only if rejected

// async/await version (same thing):
async function run() {
  const value = await myPromise;
  console.log(value);
}
run();`,
    solution: `// ── Basic Promise ──────────────────────────────────────────────\nconst successPromise = new Promise((resolve, reject) => {\n  resolve("Operation successful! 🎉");\n});\n\nsuccessPromise\n  .then(result => console.log("✓", result))\n  .catch(error => console.log("✗", error));\n\n\n// ── Promise with a condition ────────────────────────────────────\nfunction checkAge(age) {\n  return new Promise((resolve, reject) => {\n    if (age >= 18) {\n      resolve("Access granted — you are " + age);\n    } else {\n      reject("Access denied — must be 18+");\n    }\n  });\n}\n\ncheckAge(20).then(msg => console.log(msg)).catch(err => console.log(err));\ncheckAge(15).then(msg => console.log(msg)).catch(err => console.log(err));\n\n\n// ── Simulating a delayed response ──────────────────────────────\nfunction fetchUserData(userId) {\n  return new Promise((resolve) => {\n    // Simulating a brief delay (normally this would fetch from a server)\n    setTimeout(() => {\n      resolve({ id: userId, name: "Alice", level: 5 });\n    }, 50);\n  });\n}\n\nfetchUserData(1)\n  .then(user => console.log("User data:", user));\n\n\n// ── Async/Await syntax ──────────────────────────────────────────\nasync function loadProfile() {\n  try {\n    const user = await fetchUserData(42);\n    console.log("Async/await result:", user);\n    console.log("User name:", user.name);\n  } catch (err) {\n    console.error("Failed:", err);\n  }\n}\n\nloadProfile();`,
    starterCode: `// Create a Promise that resolves with a message\nconst myPromise = new Promise((resolve, reject) => {\n  // Call resolve() with a value here\n});\n\n// Handle the result with .then()\nmyPromise.then(value => console.log(value));`,
    validationRules: [
      { type:'contains_text', value:'Promise', message:'Create a new Promise' },
      { type:'contains_text', value:'resolve', message:'Call resolve() inside the Promise' },
    ],
  },
};

// ─────────────────────────────────────────────────────────────
// EDITOR PAGE COMPONENT
// ─────────────────────────────────────────────────────────────
export default function EditorPage() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user, updateUser, showToast } = useAuth();
  const { refreshLeaderboard }          = useSocket();

  const [mission,    setMission]    = useState(null);
  const [code,       setCode]       = useState('');
  const [tab,        setTab]        = useState('code');       // 'code' | 'preview'
  const [sideTab,    setSideTab]    = useState('brief');     // 'brief' | 'hint' | 'solution'
  const [feedback,   setFeedback]   = useState(null);
  const [testResults,setTestResults]= useState(null);        // run-code results
  const [tested,     setTested]     = useState(false);       // has user run tests?
  const [running,    setRunning]    = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [levelUp,    setLevelUp]    = useState(null);
  const closeLevelUp = useCallback(() => setLevelUp(null), []);
  const [showSolution, setShowSolution] = useState(false);

  const track  = id?.startsWith('h') ? 'html' : id?.startsWith('c') ? 'css' : 'javascript';
  const nextId = getNextMissionId(id);

  // ── Load mission ─────────────────────────────────────────────
  useEffect(() => {
    setFeedback(null);
    setTestResults(null);
    setTested(false);
    setShowSolution(false);
    setSideTab('brief');
    setTab('code');

    missionsAPI.getOne(id)
      .then(r => {
        const m = r.data.mission;
        // Merge solution/hint from local data if API doesn't have them
        const local = LOCAL_MISSIONS[id];
        setMission({ ...m, solution: m.solution || local?.solution, hint: m.hint || local?.hint });
        setCode(m.starterCode || local?.starterCode || '');
      })
      .catch(() => {
        const local = LOCAL_MISSIONS[id];
        if (local) { setMission(local); setCode(local.starterCode || ''); }
        else navigate('/missions');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // ── Award mission (local fallback) ───────────────────────────
  const awardLocally = useCallback(() => {
    if (!mission) return;
    const already = (user.completedMissions || []).some(m => m?.toString() === id || m === id);
    if (already) {
      setFeedback({ passed: true, firstCompletion: false });
      return;
    }
    const newXP    = (user.xp    || 0) + mission.xpReward;
    const newCoins = (user.coins || 0) + mission.coinsReward;
    const newLevel = Math.floor(newXP / 500) + 1;
    const newDone  = [...(user.completedMissions || []), id];
    const newBadges = [...(user.badges || [])];
    if (mission.badgeReward?.id && !newBadges.some(b => b.id === mission.badgeReward.id))
      newBadges.push(mission.badgeReward);
    updateUser({ xp: newXP, coins: newCoins, level: newLevel, completedMissions: newDone, badges: newBadges });
    if (newLevel > (user.level || 1)) setLevelUp(newLevel);
    setFeedback({ passed: true, firstCompletion: true,
                  xpEarned: mission.xpReward, coinsEarned: mission.coinsReward,
                  badgeEarned: mission.badgeReward?.id ? mission.badgeReward : null });
    showToast('Mission Complete! 🎉', `+${mission.xpReward} XP & +${mission.coinsReward} coins!`, 'success');
    if (mission.badgeReward?.id)
      setTimeout(() => showToast('🏅 Badge Unlocked!', `${mission.badgeReward.icon} ${mission.badgeReward.name}!`, 'gold'), 1200);
    refreshLeaderboard();
  }, [mission, id, user, updateUser, showToast, refreshLeaderboard]);

  // ── RUN & TEST (like LeetCode "Run Code") ────────────────────
  // Validates code against all rules and shows per-check results
  // Does NOT award XP — that only happens on Submit
  const handleRun = useCallback(async () => {
    if (!mission || running) return;
    setRunning(true);
    setTestResults(null);
    try {
      // Try backend first
      const { data } = await missionsAPI.submit(id, code);
      const rules = mission.validationRules || LOCAL_MISSIONS[id]?.validationRules || [];
      // Build per-rule results from backend response
      const perRule = rules.map((r, i) => ({
        label: r.message || `Check ${i + 1}`,
        passed: data.passed || false,
      }));
      if (!data.passed && data.errors?.length) {
        const errSet = new Set(data.errors);
        perRule.forEach(r => { r.passed = !errSet.has(r.label); });
      } else if (data.passed) {
        perRule.forEach(r => { r.passed = true; });
      }
      setTestResults({ passed: data.passed, checks: perRule, fromBackend: true });
      setTested(true);
    } catch {
      // Client-side validation
      const rules = mission.validationRules || LOCAL_MISSIONS[id]?.validationRules || [];
      const checks = rules.map(r => {
        let pass = false;
        try {
          switch (r.type) {
            case 'contains_tag':  pass = new RegExp(`<${r.value}[\\s>/]`, 'i').test(code); break;
            case 'contains_attr': pass = new RegExp(`${r.value}\\s*=`, 'i').test(code); break;
            case 'contains_text': pass = code.toLowerCase().includes((r.value||'').toLowerCase()); break;
            case 'css_property':  pass = new RegExp(`${r.value}\\s*:`, 'i').test(code); break;
            case 'js_output':     pass = code.includes(r.value||''); break;
            case 'regex':         pass = new RegExp(r.value, 'i').test(code); break;
            default:              pass = true;
          }
        } catch { pass = false; }
        return { label: r.message || `Check`, passed: pass };
      });
      const allPassed = checks.every(c => c.passed);
      setTestResults({ passed: allPassed, checks });
      setTested(true);
    }
    setRunning(false);
    setSideTab('brief'); // keep brief visible, test results shown in editor panel
  }, [mission, id, code, running]);

  // ── Submit / validate ────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!mission || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await missionsAPI.submit(id, code);
      if (data.passed) {
        setFeedback({ passed: true, ...data });
        if (data.firstCompletion) {
          showToast('Mission Complete! 🎉', `+${data.xpEarned} XP & +${data.coinsEarned} coins!`, 'success');
          if (data.badgeEarned?.id) setTimeout(() => showToast('🏅 Badge Unlocked!', `${data.badgeEarned.icon} ${data.badgeEarned.name}!`, 'gold'), 1200);
          if (data.newLevel > user.level) setLevelUp(data.newLevel);
          updateUser({ xp: data.newXP, coins: data.newCoins, level: data.newLevel });
          refreshLeaderboard();
        }
      } else {
        setFeedback({ passed: false, errors: data.errors });
      }
    } catch {
      // Backend unavailable — validate client-side
      const rules  = mission.validationRules || [];
      const result = validateCode(code, rules);
      if (result.passed) awardLocally();
      else setFeedback({ passed: false, errors: result.errors });
    } finally { setSubmitting(false); }
  }, [mission, id, code, user, updateUser, showToast, refreshLeaderboard, awardLocally, submitting]);

  // ── Navigate to next mission ──────────────────────────────────
  const goNext = useCallback(() => {
    if (nextId) navigate(`/missions/${nextId}`);
  }, [nextId, navigate]);

  const getLang = () => track === 'javascript' ? 'javascript' : 'html';
  const isCompleted = feedback?.passed;

  if (loading) return <div className="loading-screen"><div className="loader" /></div>;
  if (!mission) return null;

  // ── Solution panel content ────────────────────────────────────
  const solutionCode = mission.solution || LOCAL_MISSIONS[id]?.solution || '// No solution available for this mission.';

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#0a0a0f' }}>
      {levelUp && <LevelUpOverlay level={levelUp} onClose={closeLevelUp} />}

      {/* ── Top bar ─────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 16px', flexShrink:0,
                    background:'rgba(13,17,23,0.98)', borderBottom:'1px solid rgba(0,245,255,0.12)' }}>
        <button onClick={() => navigate('/missions')} style={btnStyle('ghost')}>← BACK</button>
        <div style={{ fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, color:'#fff', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {mission.title}
        </div>
        <span style={{ color:'#7c3aed', fontSize:'15px', fontWeight:700, flexShrink:0 }}>⚡{mission.xpReward}</span>
        <span style={{ color:'#ffd700', fontSize:'15px', fontWeight:700, flexShrink:0 }}>🪙{mission.coinsReward}</span>
        {mission.badgeReward?.id && <span style={{ color:'#ffd700', fontSize:'15px', flexShrink:0 }}>🏅</span>}
        <button onClick={handleRun} disabled={running || submitting} style={btnStyle('run')}>
          {running ? '⏳ TESTING...' : '▶ RUN TESTS'}
        </button>
        <button onClick={handleSubmit} disabled={submitting || !tested} title={!tested ? 'Run tests first!' : 'Submit for XP & coins'}
          style={{...btnStyle('validate'), opacity: tested ? 1 : 0.4, cursor: tested ? 'pointer' : 'not-allowed'}}>
          {submitting ? '...' : '🚀 SUBMIT'}
        </button>
        {isCompleted && nextId && (
          <button onClick={goNext} style={btnStyle('next')}>
            NEXT MISSION →
          </button>
        )}
      </div>

      {/* ── Main layout ─────────────────────────────────────── */}
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

        {/* Left sidebar */}
        <div style={{ width:'300px', flexShrink:0, background:'rgba(13,17,23,0.95)',
                      borderRight:'1px solid rgba(0,245,255,0.1)', display:'flex', flexDirection:'column', overflowY:'auto' }}>

          {/* Sidebar tabs: Brief | Hint | Solution */}
          <div style={{ display:'flex', borderBottom:'1px solid rgba(0,245,255,0.1)', flexShrink:0 }}>
            {['brief','hint','solution'].map(t => (
              <div key={t} onClick={() => setSideTab(t)}
                style={{
                  flex:1, padding:'9px 4px', textAlign:'center', cursor:'pointer',
                  fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, letterSpacing:'1px',
                  color: sideTab === t ? '#00f5ff' : 'rgba(255,255,255,0.35)',
                  borderBottom:`2px solid ${sideTab === t ? '#00f5ff' : 'transparent'}`,
                  background: sideTab === t ? 'rgba(0,245,255,0.04)' : 'transparent',
                  transition:'all 0.18s',
                }}>
                {t === 'brief' ? '📋 BRIEF' : t === 'hint' ? '💡 HINT' : '👁️ SOLUTION'}
              </div>
            ))}
          </div>

          {/* Brief panel */}
          {(sideTab === 'brief') && (
            <div style={{ padding:'16px', flex:1 }}>
              <div style={{ fontSize:'15px', color:'rgba(255,255,255,0.6)', lineHeight:1.7 }}
                dangerouslySetInnerHTML={{ __html: fmtInst(mission.instructions) }} />
            </div>
          )}

          {/* Hint panel */}
          {sideTab === 'hint' && (
            <div style={{ padding:'16px', flex:1 }}>
              <div style={{ background:'rgba(255,215,0,0.06)', border:'1px solid rgba(255,215,0,0.2)',
                            borderRadius:'8px', padding:'14px' }}>
                <div style={{ fontSize:'15px', fontWeight:700, color:'#ffd700', letterSpacing:'2px', marginBottom:'8px' }}>💡 HINT</div>
                <div style={{ fontSize:'15px', color:'rgba(255,255,255,0.65)', lineHeight:1.7, fontFamily:'var(--font-mono)' }}>
                  {mission.hint || LOCAL_MISSIONS[id]?.hint || 'No hint available.'}
                </div>
              </div>
              <div style={{ marginTop:'14px', padding:'12px', background:'rgba(255,255,255,0.03)', borderRadius:'8px', fontSize:'15px', color:'rgba(255,255,255,0.35)', lineHeight:1.6 }}>
                Still stuck? Click the <strong style={{ color:'#00f5ff' }}>👁️ SOLUTION</strong> tab to see the complete working code. Study it, understand it, then try again!
              </div>
            </div>
          )}

          {/* Solution panel */}
          {sideTab === 'solution' && (
            <div style={{ padding:'16px', flex:1, display:'flex', flexDirection:'column', gap:'12px' }}>
              <div style={{ background:'rgba(255,68,68,0.06)', border:'1px solid rgba(255,68,68,0.2)',
                            borderRadius:'8px', padding:'12px', fontSize:'15px', color:'rgba(255,200,200,0.7)', lineHeight:1.5 }}>
                ⚠️ Try solving it yourself first! Viewing the solution is fine for learning — just make sure you understand <em>why</em> it works.
              </div>
              <div style={{ fontSize:'15px', fontWeight:700, color:'#39ff14', letterSpacing:'2px', marginBottom:'4px' }}>✅ COMPLETE SOLUTION</div>
              <div style={{ background:'rgba(8,10,14,0.95)', border:'1px solid rgba(0,245,255,0.15)',
                            borderRadius:'8px', padding:'14px', overflowX:'auto', flex:1 }}>
                <pre style={{ margin:0, fontFamily:'var(--font-mono)', fontSize:'15px', color:'#e2e8f0',
                              lineHeight:1.7, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
                  {solutionCode}
                </pre>
              </div>
              <button onClick={() => { setCode(solutionCode); setTab('code'); setSideTab('brief'); }}
                style={{ padding:'10px', background:'rgba(57,255,20,0.1)', border:'1px solid rgba(57,255,20,0.3)',
                         borderRadius:'6px', color:'#39ff14', cursor:'pointer',
                         fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, letterSpacing:'1px' }}>
                📋 COPY TO EDITOR
              </button>
            </div>
          )}

          {/* Feedback box */}
          {feedback && (
            <div style={{ margin:'0 14px 14px', background: feedback.passed?'rgba(57,255,20,0.06)':'rgba(255,68,68,0.06)',
                          border:`1px solid ${feedback.passed?'rgba(57,255,20,0.3)':'rgba(255,68,68,0.3)'}`,
                          borderRadius:'8px', padding:'14px', flexShrink:0 }}>
              <div style={{ fontWeight:700, fontSize:'15px', marginBottom:'8px',
                            color: feedback.passed ? '#39ff14' : '#ff6464' }}>
                {feedback.passed
                  ? (feedback.firstCompletion ? '✓ ALL CHECKS PASSED!' : '✓ Already completed!')
                  : `✗ ${feedback.errors?.length || 0} check(s) failed`}
              </div>
              {feedback.passed && feedback.firstCompletion && (
                <div style={{ fontSize:'15px', color:'rgba(255,255,255,0.6)', display:'flex', flexDirection:'column', gap:'4px' }}>
                  <span>+{feedback.xpEarned} XP &nbsp;·&nbsp; +{feedback.coinsEarned} coins 🎉</span>
                  {feedback.badgeEarned?.id && (
                    <span style={{ color:'#ffd700' }}>🏅 {feedback.badgeEarned.icon} {feedback.badgeEarned.name}</span>
                  )}
                </div>
              )}
              {!feedback.passed && (
                <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                  {(feedback.errors || []).map((e, i) => (
                    <div key={i} style={{ fontSize:'15px', color:'rgba(255,255,255,0.5)' }}>• {e}</div>
                  ))}
                  <div style={{ marginTop:'8px', fontSize:'15px', color:'rgba(255,215,0,0.6)' }}>
                    💡 Check the <strong>HINT</strong> or <strong>SOLUTION</strong> tab for help!
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Next mission button (in sidebar too) */}
          {isCompleted && nextId && (
            <div style={{ padding:'0 14px 14px', flexShrink:0 }}>
              <button onClick={goNext} style={{
                width:'100%', padding:'12px',
                background:'linear-gradient(135deg,rgba(0,245,255,0.15),rgba(57,255,20,0.1))',
                border:'1px solid rgba(57,255,20,0.4)', borderRadius:'8px',
                color:'#39ff14', cursor:'pointer',
                fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, letterSpacing:'1.5px',
                transition:'all 0.2s',
              }}>
                🎯 NEXT MISSION →
              </button>
            </div>
          )}

          {/* Back to track button when last mission */}
          {isCompleted && !nextId && (
            <div style={{ padding:'0 14px 14px', flexShrink:0 }}>
              <button onClick={() => navigate('/missions')} style={{
                width:'100%', padding:'12px',
                background:'rgba(255,215,0,0.1)', border:'1px solid rgba(255,215,0,0.4)',
                borderRadius:'8px', color:'#ffd700', cursor:'pointer',
                fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, letterSpacing:'1.5px',
              }}>
                🏆 ALL DONE! VIEW MISSIONS
              </button>
            </div>
          )}
        </div>

        {/* Right: Editor + Preview */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {/* Tab bar */}
          <div style={{ display:'flex', background:'rgba(10,10,15,0.9)', borderBottom:'1px solid rgba(0,245,255,0.1)', flexShrink:0 }}>
            {['code','preview'].map((t, i) => (
              <div key={t} onClick={() => setTab(t)} style={{
                padding:'8px 18px', cursor:'pointer',
                fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, letterSpacing:'1px',
                color: tab===t ? '#00f5ff' : 'rgba(255,255,255,0.38)',
                borderBottom:`2px solid ${tab===t ? '#00f5ff' : 'transparent'}`,
                background: tab===t ? 'rgba(0,245,255,0.04)' : 'transparent',
                transition:'all 0.18s',
              }}>
                {i === 0 ? '< CODE EDITOR >' : '◉ LIVE PREVIEW'}
              </div>
            ))}
          </div>

          {/* ── Test Results Panel (shown below tab bar when tests run) ── */}
          {testResults && (
            <div style={{
              flexShrink:0, padding:'10px 14px',
              background: testResults.passed ? 'rgba(57,255,20,0.05)' : 'rgba(255,68,68,0.05)',
              borderBottom:`1px solid ${testResults.passed ? 'rgba(57,255,20,0.25)' : 'rgba(255,68,68,0.25)'}`,
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                <span style={{ fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700,
                               color: testResults.passed ? '#39ff14' : '#ff6464', letterSpacing:'2px' }}>
                  {testResults.passed ? '✅ ALL TESTS PASSED' : '❌ SOME TESTS FAILED'}
                </span>
                <span style={{ fontSize:'15px', color:'rgba(255,255,255,0.35)', marginLeft:'auto' }}>
                  {testResults.checks.filter(c=>c.passed).length}/{testResults.checks.length} checks passed
                </span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                {testResults.checks.map((c,i) => (
                  <div key={i} style={{
                    display:'flex', alignItems:'flex-start', gap:'8px', padding:'5px 9px',
                    background: c.passed ? 'rgba(57,255,20,0.07)' : 'rgba(255,68,68,0.07)',
                    border:`1px solid ${c.passed ? 'rgba(57,255,20,0.2)' : 'rgba(255,68,68,0.2)'}`,
                    borderRadius:'5px', fontSize:'15px',
                  }}>
                    <span style={{ color: c.passed ? '#39ff14' : '#ff6464', fontWeight:700, flexShrink:0, marginTop:'1px' }}>
                      {c.passed ? '✓' : '✗'}
                    </span>
                    <span style={{ color: c.passed ? 'rgba(57,255,20,0.85)' : 'rgba(255,150,150,0.85)', lineHeight:1.5 }}>
                      {c.label}
                    </span>
                  </div>
                ))}
              </div>
              {testResults.passed && !feedback && (
                <div style={{ marginTop:'8px', padding:'7px 10px',
                              background:'rgba(57,255,20,0.07)', border:'1px solid rgba(57,255,20,0.2)',
                              borderRadius:'5px', fontSize:'15px', color:'#39ff14', textAlign:'center' }}>
                  🎉 All tests pass! Click <strong>🚀 SUBMIT</strong> to earn your XP and coins.
                </div>
              )}
              {!testResults.passed && (
                <div style={{ marginTop:'8px', padding:'7px 10px',
                              background:'rgba(255,215,0,0.05)', border:'1px solid rgba(255,215,0,0.2)',
                              borderRadius:'5px', fontSize:'15px', color:'rgba(255,215,0,0.8)' }}>
                  💡 Fix the failing checks, then click <strong>▶ RUN TESTS</strong> again.
                  Need help? Open the <strong>HINT</strong> or <strong>SOLUTION</strong> tab →
                </div>
              )}
            </div>
          )}

          {/* ── Submit locked notice (before running tests) ── */}
          {!testResults && !feedback && (
            <div style={{
              flexShrink:0, padding:'7px 14px',
              background:'rgba(0,245,255,0.03)', borderBottom:'1px solid rgba(0,245,255,0.08)',
              display:'flex', alignItems:'center', gap:'8px',
            }}>
              <span style={{ fontSize:'15px', color:'rgba(255,255,255,0.3)', lineHeight:1.5 }}>
                📋 Write your code, then click <strong style={{color:'#39ff14'}}>▶ RUN TESTS</strong> to check your solution.
                After all tests pass, <strong style={{color:'#00f5ff'}}>🚀 SUBMIT</strong> to earn XP & coins.
              </span>
            </div>
          )}

          {/* Monaco Editor */}
          {tab === 'code' && (
            <div style={{ flex:1, overflow:'hidden' }}>
              <Editor
                height="100%" language={getLang()} value={code} theme="vs-dark"
                onChange={v => setCode(v || '')}
                options={{
                  fontSize: 14, fontFamily: 'Share Tech Mono, monospace',
                  lineNumbers: 'on', minimap: { enabled: false },
                  scrollBeyondLastLine: false, automaticLayout: true,
                  padding: { top: 14 }, wordWrap: 'on', tabSize: 2,
                  bracketPairColorization: { enabled: true },
                }}
              />
            </div>
          )}

          {/* Live Preview */}
          {tab === 'preview' && (
            <div style={{ flex:1, background:'#fff', overflow:'hidden' }}>
              <iframe title="preview" style={{ width:'100%', height:'100%', border:'none' }}
                sandbox="allow-scripts allow-same-origin"
                srcDoc={
                  track === 'javascript'
                    ? `<!DOCTYPE html><html><body style="background:#1a1a2e;color:#e2e8f0;font-family:Share Tech Mono,monospace;padding:16px;margin:0"><pre style="white-space:pre-wrap">${escapeHtml(code)}</pre></body></html>`
                    : code
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────
function btnStyle(type) {
  const base = { cursor:'pointer', fontFamily:'var(--font-game)', fontWeight:700, letterSpacing:'1px', border:'1px solid', borderRadius:'5px', transition:'all 0.18s', flexShrink:0 };
  switch(type) {
    case 'ghost':    return { ...base, padding:'6px 12px', fontSize:'15px', background:'rgba(255,255,255,0.04)', borderColor:'rgba(255,255,255,0.09)', color:'rgba(255,255,255,0.6)' };
    case 'run':      return { ...base, padding:'8px 18px', fontSize:'15px', background:'rgba(0,245,255,0.1)', borderColor:'rgba(0,245,255,0.35)', color:'#00f5ff' };
    case 'validate': return { ...base, padding:'8px 18px', fontSize:'15px', background:'rgba(57,255,20,0.11)', borderColor:'rgba(57,255,20,0.35)', color:'#39ff14' };
    case 'next':     return { ...base, padding:'8px 18px', fontSize:'15px', background:'linear-gradient(135deg,rgba(0,245,255,0.15),rgba(57,255,20,0.1))', borderColor:'rgba(57,255,20,0.5)', color:'#39ff14' };
    default:         return base;
  }
}

function fmtInst(html) {
  if (!html) return '';
  const styles = `<style>
    .mi-track{font-family:Orbitron,monospace;font-size:9px;font-weight:700;color:rgba(0,245,255,0.5);letter-spacing:3px;margin-bottom:8px}
    .mi-title{font-family:Orbitron,monospace;font-size:17px;font-weight:700;color:#fff;margin-bottom:14px;line-height:1.3;border-bottom:1px solid rgba(0,245,255,0.15);padding-bottom:10px}
    .mi-what{background:rgba(0,245,255,0.04);border-left:3px solid rgba(0,245,255,0.4);border-radius:0 6px 6px 0;padding:12px 14px;margin-bottom:14px}
    .mi-section-label{font-family:Orbitron,monospace;font-size:9px;font-weight:700;color:rgba(0,245,255,0.6);letter-spacing:2px;margin-bottom:8px;margin-top:14px}
    .mi-steps{list-style:none;padding:0;margin:10px 0 14px;display:flex;flex-direction:column;gap:10px}
    .mi-steps li{display:flex;gap:10px;align-items:flex-start;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.07);border-radius:8px;padding:10px 12px}
    .mi-step-num{background:rgba(0,245,255,0.15);border:1px solid rgba(0,245,255,0.3);color:#00f5ff;font-family:Orbitron,monospace;font-size:11px;font-weight:700;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
    .mi-steps pre{background:rgba(8,10,14,0.8);border:1px solid rgba(0,245,255,0.12);border-radius:5px;padding:8px 10px;margin:7px 0 0;font-family:Share Tech Mono,monospace;font-size:11px;color:#a8d8ff;overflow-x:auto;white-space:pre-wrap;word-break:break-word}
    .mi-note{font-size:11px;color:rgba(255,255,255,0.38);display:block;margin-top:4px;line-height:1.5}
    .mi-structure pre{background:rgba(8,10,14,0.8);border:1px solid rgba(0,245,255,0.12);border-radius:7px;padding:12px;font-family:Share Tech Mono,monospace;font-size:11px;color:#a8d8ff;overflow-x:auto;white-space:pre;line-height:1.7}
    .mi-example pre{background:rgba(8,10,14,0.8);border:1px solid rgba(57,255,20,0.15);border-radius:7px;padding:12px;font-family:Share Tech Mono,monospace;font-size:11px;color:#a8d8ff;overflow-x:auto;white-space:pre-wrap;margin:8px 0}
    .mi-table{width:100%;border-collapse:collapse;font-size:11px;margin-top:8px}
    .mi-table th{background:rgba(0,245,255,0.1);color:#00f5ff;font-family:Orbitron,monospace;font-size:9px;letter-spacing:1px;padding:7px 10px;text-align:left;border:1px solid rgba(0,245,255,0.15)}
    .mi-table td{padding:7px 10px;border:1px solid rgba(255,255,255,0.07);color:rgba(255,255,255,0.6);vertical-align:top}
    .mi-table tr:nth-child(even) td{background:rgba(255,255,255,0.02)}
    .mi-note-box{background:rgba(255,215,0,0.05);border:1px solid rgba(255,215,0,0.2);border-radius:7px;padding:10px 13px;margin:12px 0;font-size:12px;color:rgba(255,255,255,0.55);line-height:1.6}
    .mi-check{background:rgba(57,255,20,0.04);border:1px solid rgba(57,255,20,0.18);border-radius:7px;padding:12px 14px;margin-top:14px}
    .mi-check ul{list-style:none;padding:0;margin:6px 0 0;display:flex;flex-direction:column;gap:6px}
    .mi-check li{font-size:12px;color:rgba(255,255,255,0.6);padding:4px 0}
    p{color:rgba(255,255,255,0.6);font-size:13px;line-height:1.7;margin:6px 0}
    ul:not(.mi-steps){padding-left:18px;margin:6px 0}
    ul:not(.mi-steps) li{margin-bottom:5px;font-size:13px;color:rgba(255,255,255,0.6)}
    code{background:rgba(0,245,255,0.09);border:1px solid rgba(0,245,255,0.2);border-radius:3px;padding:1px 5px;font-family:Share Tech Mono,monospace;font-size:11px;color:#00f5ff}
    strong{color:#ffd700} em{color:rgba(255,255,255,0.75);font-style:italic}
    pre{background:rgba(8,10,14,0.8);border:1px solid rgba(0,245,255,0.12);border-radius:5px;padding:8px 10px;font-family:Share Tech Mono,monospace;font-size:11px;color:#a8d8ff;overflow-x:auto;white-space:pre-wrap;word-break:break-word;margin:7px 0}
  </style>`;
  return styles + html;
}
